// src/App.js
import React, { useState, useEffect, useCallback } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { escutarEventos, escutarIngressos, escutarIngresso, escutarParticipantesEvento } from "./services/firebase";
import { criarPagamento, buscarIngresso, meuIngressos, criarEvento, atualizarEvento, excluirEvento, excluirIngresso, realizarCheckinBackend as realizarCheckin, getMpOAuthUrl, getMpOAuthStatus } from "./services/api";
import { QRCodeSVG } from "qrcode.react";
import { Html5QrcodeScanner } from "html5-qrcode";
import { Routes, Route, useNavigate, useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ClientDashboard from "./pages/ClientDashboard";
import HistoryRCC from "./pages/HistoryRCC";
import RefundPolicy from "./pages/RefundPolicy";
import SecurityPolicy from "./pages/SecurityPolicy";
import TermsOfUse from "./pages/TermsOfUse";
import PrivacyPolicy from "./pages/PrivacyPolicy";

/* ─────────────────────────────────────────────────────────────────────────────
   ESTILOS GLOBAIS
───────────────────────────────────────────────────────────────────────────── */
const G = `
  @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;500;600;700;800&family=Poppins:wght@400;500;600;700;800&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{background:#F0F4F8;font-family:'Open Sans',sans-serif;color:#004058}
  input,select,textarea,button{font-family:inherit}
  input:focus,select:focus,textarea:focus{outline:none;border-color:#004058!important;box-shadow:0 0 0 3px rgba(0,64,88,.07)}
  ::-webkit-scrollbar{width:5px}::-webkit-scrollbar-thumb{background:#ccc;border-radius:3px}
  @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  @keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
  .fu{animation:fadeUp .4s ease both}.fu2{animation:fadeUp .4s .07s ease both}
  .fu3{animation:fadeUp .4s .14s ease both}.fu4{animation:fadeUp .4s .21s ease both}
  .fi{animation:fadeIn .25s ease both}
  .hov{transition:transform .2s,box-shadow .2s;cursor:pointer}
  .hov:hover{transform:translateY(-3px);box-shadow:0 16px 48px rgba(0,0,0,.13)!important}
  .bt{transition:opacity .15s,transform .1s}.bt:hover{opacity:.85}.bt:active{transform:scale(.97)}
  @media(max-width: 600px){
    .hide-mob{display:none!important}
    .mob-col{grid-template-columns:1fr!important}
    .mob-pd{padding:16px!important}
    .mob-grid{grid-template-columns:repeat(auto-fill, minmax(100%, 1fr))!important}
  }
  .print-only { display: none !important; }
  @media print {
    body * { visibility: hidden; }
    .print-area, .print-area * { visibility: visible; }
    .print-area { position: absolute; left: 0; top: 0; width: 100%; }
    .no-print { display: none !important; }
    .print-only { display: block !important; }
  }
`;

const K = {
  bg:"#F0F4F8",sur:"#FFF",alt:"#E2E8F0",brd:"#CBD5E1",
  ink:"#004058",mid:"#475569",lt:"#94A3B8",
  red:"#ef5350",redBg:"#ffebee",
  grn:"#007f48",grnBg:"#e8f5e9",
  amb:"#D97706",ambBg:"#FEF3C7",
  blu:"#0284C7",bluBg:"#F0F9FF",
  mp:"#009EE3",
  r:{s:8,m:12,l:16,xl:20,xxl:28,p:999},
  sh:{s:"0 1px 4px rgba(0,0,0,.05)",m:"0 4px 16px rgba(0,0,0,.08)",l:"0 12px 40px rgba(0,0,0,.11)",xl:"0 24px 72px rgba(0,0,0,.16)"},
};

/* ─── Átomos UI ─────────────────────────────────────────────────────────────── */
const Chip = ({c=K.mid,bg=K.alt,children,sx})=>(
  <span style={{display:"inline-flex",alignItems:"center",gap:3,background:bg,color:c,borderRadius:K.r.p,padding:"3px 10px",fontSize:12,fontWeight:600,...sx}}>{children}</span>
);

const Btn = ({v="pri",sz="md",full,loading,children,sx,...p})=>{
  const S={sm:{padding:"8px 16px",fontSize:13},md:{padding:"11px 22px",fontSize:14},lg:{padding:"14px 30px",fontSize:15},xl:{padding:"17px 40px",fontSize:16}};
  const V={pri:{background:K.ink,color:"#fff",border:"none"},red:{background:K.red,color:"#fff",border:"none"},out:{background:"transparent",color:K.mid,border:`1.5px solid ${K.brd}`},ghost:{background:"transparent",color:K.mid,border:"none"},mp:{background:K.mp,color:"#fff",border:"none"},grn:{background:K.grn,color:"#fff",border:"none"}};
  return <button className="bt" disabled={loading||p.disabled} style={{display:"inline-flex",alignItems:"center",justifyContent:"center",gap:7,borderRadius:K.r.m,fontWeight:700,cursor:(loading||p.disabled)?"not-allowed":"pointer",width:full?"100%":undefined,opacity:(loading||p.disabled)?.6:1,...S[sz],...V[v],...sx}} {...p}>
    {loading?<><span style={{width:14,height:14,border:"2px solid currentColor",borderTopColor:"transparent",borderRadius:"50%",animation:"spin .7s linear infinite",display:"inline-block"}}/>Aguarde...</>:children}
  </button>;
};

const Inp = ({label,error,...p})=>(
  <div style={{marginBottom:14}}>
    {label&&<label style={{display:"block",fontSize:12,fontWeight:700,color:K.mid,marginBottom:5,textTransform:"uppercase",letterSpacing:"0.4px"}}>{label}</label>}
    <input style={{width:"100%",border:`1.5px solid ${error?K.red:K.brd}`,borderRadius:K.r.m,padding:"12px 14px",fontSize:15,color:K.ink,background:K.sur,transition:"border .15s"}} {...p}/>
    {error&&<p style={{color:K.red,fontSize:12,marginTop:3}}>{error}</p>}
  </div>
);

const Modal = ({children,onClose,mw=520})=>(
  <div className="fi" style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",backdropFilter:"blur(10px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:16}} onClick={e=>{if(e.target===e.currentTarget)onClose?.();}}>
    <div style={{background:K.sur,borderRadius:K.r.xxl,width:"100%",maxWidth:mw,maxHeight:"92vh",overflowY:"auto",boxShadow:K.sh.xl}} onClick={e=>e.stopPropagation()}>
      {children}
    </div>
  </div>
);

const Toast = ({msg,type,onClose})=>(
  <div className="fi" style={{position:"fixed",bottom:26,right:26,zIndex:9999,background:type==="error"?"#DC2626":type==="success"?K.grn:K.ink,color:"#fff",borderRadius:K.r.l,padding:"12px 20px",fontWeight:600,fontSize:14,boxShadow:K.sh.xl,display:"flex",alignItems:"center",gap:8,maxWidth:340,cursor:"pointer"}} onClick={onClose}>
    {type==="success"?"✅":type==="error"?"⚠️":"ℹ️"} {msg}
  </div>
);

const Spinner = ()=>(
  <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:48}}>
    <div style={{width:32,height:32,border:"3px solid #E3DFD9",borderTopColor:K.ink,borderRadius:"50%",animation:"spin .8s linear infinite"}}/>
  </div>
);

const fmtD = d=>{if(!d)return"";const dt=new Date(d+"T00:00:00");return dt.toLocaleDateString("pt-BR",{weekday:"long",day:"numeric",month:"long",year:"numeric"});};
const fmtP = p=>p===0?"Gratuito":`R$ ${p.toFixed(2).replace(".",",")}`;
const getQR = d=>`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(d)}&color=111111&bgcolor=ffffff&margin=10`;
const isValidImage = img => typeof img === "string" && (img.startsWith("http") || img.startsWith("data:") || img.startsWith("/"));


/* ─── Modal de Autenticação ─────────────────────────────────────────────────── */
function AuthModal({onClose,onSuccess}){
  const {cadastrar,entrar,entrarComGoogle,vincularGoogleComSenha,recuperarSenha}=useAuth();
  const [modo,setModo]=useState("login");
  const [f,setF]=useState({nome:"",email:"",senha:"",cpf:"",telefone:""});
  const [erros,setErros]=useState({});
  const [loading,setLoading]=useState(false);
  const [errMsg,setErrMsg]=useState("");

  const submit=async()=>{
    const e={};
    if(!f.email)e.email="Obrigatório";
    if(!f.senha||f.senha.length<6)e.senha="Mínimo 6 caracteres";
    if(modo==="cadastro"){
      if(!f.nome)e.nome="Obrigatório";
      if(!f.cpf)e.cpf="Obrigatório";
      if(!f.telefone)e.telefone="Obrigatório";
    }
    if(Object.keys(e).length){setErros(e);return;}
    setLoading(true);setErrMsg("");
    try{
      if(modo==="cadastro")await cadastrar({nome:f.nome,email:f.email,senha:f.senha,cpf:f.cpf,telefone:f.telefone});
      else await entrar({email:f.email,senha:f.senha});
      onSuccess?.();onClose?.();
    }catch(err){
      setErrMsg(err.code==="auth/email-already-in-use"?"E-mail já cadastrado.":err.code==="auth/wrong-password"||err.code==="auth/user-not-found"||err.code==="auth/invalid-credential"?"E-mail ou senha incorretos.":err.message||"Erro ao autenticar.");
    }finally{setLoading(false);}
  };

  const handleRecuperarSenha = async () => {
    if(!f.email) {
      setErros({email: "Digite seu e-mail primeiro para recuperar a senha."});
      return;
    }
    setLoading(true); setErrMsg(""); setErros({});
    try {
      await recuperarSenha(f.email);
      alert(`Um link de recuperação foi enviado para ${f.email}. Verifique sua caixa de entrada e SPAM.`);
    } catch(err) {
      setErrMsg(err.code === "auth/user-not-found" ? "Usuário não encontrado com este e-mail." : "Erro ao enviar e-mail de recuperação.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle=async()=>{
    setLoading(true);setErrMsg("");
    try{
      await entrarComGoogle();
      onSuccess?.();onClose?.();
    }catch(err){
      if(err.code==="auth/account-exists-with-different-credential"){
        const senha=prompt("Esta conta de e-mail já está cadastrada com senha.\nPor favor, digite sua senha para vincular sua conta do Google:");
        if(senha){
          try{
            await vincularGoogleComSenha(senha,err);
            alert("Conta Google vinculada com sucesso! Agora você pode entrar usando o Google.");
            onSuccess?.();onClose?.();
          }catch(linkErr){
            setErrMsg("Erro ao vincular conta: senha incorreta ou erro de rede.");
          }
        }else{
          setErrMsg("Operação cancelada. Vincule sua conta digitando a senha.");
        }
      }else{
        setErrMsg(err.message||"Erro ao autenticar com o Google.");
      }
    }finally{setLoading(false);}
  };

  return(
    <Modal onClose={onClose} mw={420}>
      <div style={{padding:28}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
          <h2 style={{fontFamily:"'Poppins',sans-serif",fontSize:22,color:K.ink}}>{modo==="login"?"Bem-vindo de volta":"Criar conta"}</h2>
          <button onClick={onClose} style={{background:K.alt,border:"none",width:28,height:28,borderRadius:8,cursor:"pointer",color:K.mid,fontSize:14}}>✕</button>
        </div>
        <div style={{display:"flex",background:K.alt,borderRadius:K.r.m,padding:3,marginBottom:20,gap:3}}>
          {[["login","Entrar"],["cadastro","Criar conta"]].map(([m,l])=>(
            <button key={m} onClick={()=>{setModo(m);setErros({});setErrMsg("");}} style={{flex:1,padding:"9px 0",borderRadius:K.r.s,border:"none",background:modo===m?K.sur:"transparent",color:modo===m?K.ink:K.lt,fontWeight:700,fontSize:13,cursor:"pointer",boxShadow:modo===m?K.sh.s:"none",transition:"all .15s"}}>{l}</button>
          ))}
        </div>
        {modo==="cadastro"&&<>
          <Inp label="Nome completo *" value={f.nome} error={erros.nome} onChange={e=>setF({...f,nome:e.target.value})} placeholder="Seu nome"/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Inp label="CPF *" value={f.cpf} error={erros.cpf} onChange={e=>setF({...f,cpf:e.target.value})} placeholder="000.000.000-00"/>
            <Inp label="WhatsApp *" value={f.telefone} error={erros.telefone} onChange={e=>setF({...f,telefone:e.target.value})} placeholder="(71) 99999-0000"/>
          </div>
        </>}
        <Inp label="E-mail *" type="email" value={f.email} error={erros.email} onChange={e=>setF({...f,email:e.target.value})} placeholder="seu@email.com"/>
        <Inp label="Senha *" type="password" value={f.senha} error={erros.senha} onChange={e=>setF({...f,senha:e.target.value})} placeholder="Mínimo 6 caracteres"/>
        
        {modo === "login" && (
          <div style={{textAlign: "right", marginTop: -6, marginBottom: 12}}>
            <button onClick={handleRecuperarSenha} type="button" style={{background: "none", border: "none", color: K.mp, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0}}>Esqueceu a senha?</button>
          </div>
        )}
        {errMsg&&<div style={{background:K.redBg,border:`1px solid ${K.red}30`,borderRadius:K.r.m,padding:"10px 14px",marginBottom:14,fontSize:13,color:K.red}}>⚠️ {errMsg}</div>}
        <Btn full sz="lg" loading={loading} onClick={submit} sx={{marginTop:4}}>
          {modo==="login"?"Entrar →":"Criar conta →"}
        </Btn>
        <div style={{display:"flex",alignItems:"center",gap:10,margin:"16px 0",opacity:.5}}>
          <div style={{flex:1,height:1,background:K.mid}}/>
          <span style={{fontSize:12,fontWeight:700}}>OU</span>
          <div style={{flex:1,height:1,background:K.mid}}/>
        </div>
        <Btn full sz="lg" loading={loading} onClick={handleGoogle} sx={{background:"#fff",color:"#444",border:"1.5px solid #E3DFD9",marginBottom:10}}>
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="G" style={{width:18}}/> Continuar com o Google
        </Btn>
        <p style={{textAlign:"center",fontSize:12,color:K.lt,marginTop:10}}>🔒 Autenticação segura</p>
      </div>
    </Modal>
  );
}

/* ─── COMPONENTE LISTA DE PARTICIPANTES ─────────────────────────────────────── */
function ParticipantesTab({ eventos }) {
  const [selectedEvId, setSelectedEvId] = useState(eventos[0]?.id || "");
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printMode, setPrintMode] = useState("list"); // "list" | "financial"

  useEffect(() => {
    if (!selectedEvId) return;
    setLoading(true);
    const unsub = escutarParticipantesEvento(selectedEvId, (list) => {
      setParts(list);
      setLoading(false);
    });
    return unsub;
  }, [selectedEvId]);

  useEffect(() => {
    const onAfterPrint = () => setPrintMode("list");
    window.addEventListener("afterprint", onAfterPrint);
    return () => window.removeEventListener("afterprint", onAfterPrint);
  }, []);

  const evt = eventos.find(e => e.id === selectedEvId);

  const handlePrintList = () => {
    setPrintMode("list");
    setTimeout(() => window.print(), 100);
  };

  const handlePrintFinancial = () => {
    setPrintMode("financial");
    setTimeout(() => window.print(), 100);
  };

  const handleExportCSV = () => {
    if (parts.length === 0) return alert("Nenhum participante para exportar.");
    const header = ["Nome", "Email", "CPF", "Telefone", "Status", "Check-in"];
    const rows = parts.map(p => [
      `"${p.nome}"`,
      `"${p.email}"`,
      `"${p.cpf || ""}"`,
      `"${p.telefone || ""}"`,
      `"${p.status}"`,
      p.utilizado ? "Sim" : "Nao"
    ]);
    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + 
      [header, ...rows].map(e => e.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `participantes_${evt?.title || "evento"}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const approvedParts = parts.filter(p => p.status === "pago" || p.status === "approved" || p.status === "confirmed");
  let totalGross = 0;
  let totalPix = 0;
  let totalCredit = 0;
  let totalDebit = 0;
  let totalOther = 0;

  approvedParts.forEach(p => {
    const val = Number(p.valorPago || p.valorTotal || 0);
    totalGross += val;
    const method = String(p.metodoPagamento || "").toLowerCase();
    if (method.includes("pix")) totalPix += val;
    else if (method.includes("credit")) totalCredit += val;
    else if (method.includes("debit")) totalDebit += val;
    else totalOther += val;
  });

  const feePerc = evt?.platformFee !== undefined ? Number(evt.platformFee) : 7.5;
  const totalFee = totalGross * (feePerc / 100);
  const totalNet = totalGross - totalFee;

  return (
    <div className={`print-area print-mode-${printMode}`}>
      <div className="no-print" style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
        <h2 style={{ fontFamily: "'Lora',serif", fontSize: 20, color: K.ink, margin: 0 }}>Gestão do Evento</h2>
        <select value={selectedEvId} onChange={e => setSelectedEvId(e.target.value)} style={{ padding: "8px 12px", borderRadius: K.r.m, border: `1px solid ${K.brd}`, background: K.sur, flex: 1, maxWidth: 300 }}>
          <option value="" disabled>Selecione um evento...</option>
          {eventos.map(e => <option key={e.id} value={e.id}>{e.emoji} {e.title}</option>)}
        </select>
        <div style={{display:"flex",gap:8,marginLeft:"auto"}}>
          <Btn sz="sm" v="out" onClick={handleExportCSV}>Exportar CSV</Btn>
          <Btn sz="sm" v="out" onClick={handlePrintList}>Imprimir Lista</Btn>
          <Btn sz="sm" onClick={handlePrintFinancial}>Imprimir Financeiro</Btn>
        </div>
      </div>

      {loading && <p className="no-print" style={{ color: K.mid }}>Carregando lista...</p>}

      {!loading && parts.length === 0 && (
        <div className="no-print" style={{ background: K.sur, padding: 30, borderRadius: K.r.l, textAlign: "center", color: K.mid, border: `1px solid ${K.brd}` }}>
          Nenhum ingresso emitido para este evento ainda.
        </div>
      )}

      {/* PRINT: FINANCIAL REPORT */}
      {!loading && parts.length > 0 && printMode === "financial" && (
        <div className="only-financial">
          <h1 style={{fontSize:24,marginBottom:10,color:K.ink}}>{evt?.title} - Relatório Financeiro</h1>
          <p style={{marginBottom:24,color:K.mid}}>Gerado em {new Date().toLocaleString("pt-BR")}</p>
          
          <h3 style={{fontSize:18,marginBottom:14,color:K.ink}}>Estatísticas de Ocupação</h3>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16,marginBottom:32}}>
            <div style={{border:`1px solid ${K.brd}`,padding:20,borderRadius:K.r.m}}>
              <p style={{fontSize:12,color:K.mid,textTransform:"uppercase"}}>Inscritos (Vendidos)</p>
              <p style={{fontSize:24,fontWeight:700,color:K.ink}}>{parts.length}</p>
            </div>
            <div style={{border:`1px solid ${K.brd}`,padding:20,borderRadius:K.r.m}}>
              <p style={{fontSize:12,color:K.mid,textTransform:"uppercase"}}>Lotação Ocupada</p>
              <p style={{fontSize:24,fontWeight:700,color:K.ink}}>{evt?.spots ? Math.round((parts.length / evt.spots)*100) : 0}%</p>
            </div>
            <div style={{border:`1px solid ${K.brd}`,padding:20,borderRadius:K.r.m}}>
              <p style={{fontSize:12,color:K.mid,textTransform:"uppercase"}}>Vagas Restantes</p>
              <p style={{fontSize:24,fontWeight:700,color:K.ink}}>{evt?.spots ? Math.max(0, evt.spots - parts.length) : 0}</p>
            </div>
          </div>

          <h3 style={{fontSize:18,marginBottom:14,color:K.ink}}>Balanço Contábil</h3>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16,marginBottom:32}}>
            <div style={{border:`1px solid ${K.brd}`,padding:20,borderRadius:K.r.m}}>
              <p style={{fontSize:12,color:K.mid,textTransform:"uppercase"}}>Receita Bruta</p>
              <p style={{fontSize:24,fontWeight:700,color:K.ink}}>{new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(totalGross)}</p>
            </div>
            <div style={{border:`1px solid ${K.brd}`,padding:20,borderRadius:K.r.m}}>
              <p style={{fontSize:12,color:K.mid,textTransform:"uppercase"}}>Taxa da Plataforma ({feePerc}%)</p>
              <p style={{fontSize:24,fontWeight:700,color:K.red}}>- {new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(totalFee)}</p>
            </div>
            <div style={{border:`1px solid ${K.brd}`,padding:20,borderRadius:K.r.m,background:K.grnBg}}>
              <p style={{fontSize:12,color:K.grn,textTransform:"uppercase"}}>Receita Líquida</p>
              <p style={{fontSize:24,fontWeight:700,color:K.grn}}>{new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(totalNet)}</p>
            </div>
          </div>

          <h3 style={{fontSize:18,marginBottom:14,color:K.ink}}>Receita por Método de Pagamento</h3>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <tbody>
              <tr style={{borderBottom:`1px solid ${K.brd}`}}>
                <td style={{padding:"12px 0",fontWeight:600}}>Pix</td>
                <td style={{padding:"12px 0",textAlign:"right"}}>{new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(totalPix)}</td>
              </tr>
              <tr style={{borderBottom:`1px solid ${K.brd}`}}>
                <td style={{padding:"12px 0",fontWeight:600}}>Cartão de Crédito</td>
                <td style={{padding:"12px 0",textAlign:"right"}}>{new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(totalCredit)}</td>
              </tr>
              <tr style={{borderBottom:`1px solid ${K.brd}`}}>
                <td style={{padding:"12px 0",fontWeight:600}}>Cartão de Débito</td>
                <td style={{padding:"12px 0",textAlign:"right"}}>{new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(totalDebit)}</td>
              </tr>
              <tr style={{borderBottom:`1px solid ${K.brd}`}}>
                <td style={{padding:"12px 0",fontWeight:600}}>Outros (Isenções, etc)</td>
                <td style={{padding:"12px 0",textAlign:"right"}}>{new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(totalOther)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* PRINT: LIST / SCREEN: LIST */}
      {!loading && parts.length > 0 && printMode === "list" && (
        <div className="only-list">
          <div className="print-only" style={{marginBottom:20}}>
            <h1 style={{fontSize:24,marginBottom:8,color:K.ink}}>{evt?.title} - Lista de Conferência</h1>
            <p style={{color:K.mid}}>Total de Aprovados: {approvedParts.length}</p>
          </div>

          <div className="no-print" style={{ display: "flex", gap: 16, fontSize: 13, color: K.mid, marginBottom: 10 }}>
            <span>Total: <strong>{parts.length}</strong></span>
            <span>Aprovados: <strong>{approvedParts.length}</strong></span>
            <span>Check-in: <strong>{parts.filter(p => p.utilizado).length}</strong></span>
          </div>
          {parts.map(p => (
            <div key={p.id} style={{ background: K.sur, border: `1px solid ${K.brd}`, borderRadius: K.r.m, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{display:"flex", alignItems:"center", gap: 12}}>
                <div className="print-only" style={{width: 24, height: 24, border: "2px solid #000", borderRadius: 4, flexShrink: 0}}></div>
                <div>
                  <p style={{ fontWeight: 700, color: K.ink, fontSize: 15, marginBottom: 2 }}>{p.nome}</p>
                  <p style={{ fontSize: 13, color: K.mid }}>{p.email} • {p.cpf ? `CPF: ${p.cpf}` : ""}</p>
                </div>
              </div>
              <div className="no-print" style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                <div>
                  {p.utilizado ? (
                    <Chip color={K.grnBg} textColor={K.grn}>Check-in OK</Chip>
                  ) : p.status === "pago" || p.status === "approved" || p.status === "confirmed" ? (
                    <Chip color={K.grnBg} textColor={K.grn}>Pagamento Aprovado</Chip>
                  ) : (
                    <Chip color={`${K.mp}20`} textColor={K.mp}>Pendente</Chip>
                  )}
                </div>
                <div style={{display: "flex", alignItems: "center", gap: 10}}>
                  <p style={{ fontSize: 11, color: K.lt, margin: 0 }}>Ref: {p.id.slice(-6).toUpperCase()}</p>
                  <button onClick={async () => {
                    if(!window.confirm(`Tem certeza que deseja cancelar a inscrição de ${p.nome}?`)) return;
                    try {
                      await excluirIngresso(p.id);
                      alert("Inscrição cancelada com sucesso.");
                    } catch(err) {
                      alert("Erro: " + err.message);
                    }
                  }} style={{ background: "none", border: "none", color: K.red, cursor: "pointer", fontSize: 12, textDecoration: "underline" }}>Cancelar</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EventMPStatus({ ev, user }) {
  const [mpStatus, setMpStatus] = useState(null);
  
  useEffect(() => {
    getMpOAuthStatus(ev.id)
      .then(d => setMpStatus(d))
      .catch(e => console.error("Erro OAuth", e));
  }, [ev.id]);

  const connectMP = async () => {
    try {
      const data = await getMpOAuthUrl(ev.id);
      if (data.url) window.location.href = data.url;
    } catch(err) {
      alert("Erro ao gerar URL do Mercado Pago.");
    }
  };

  if (mpStatus === null) return <span style={{fontSize:12,color:K.lt}}>Verificando MP...</span>;
  
  if (mpStatus.connected) {
    return <span style={{fontSize:13,color:K.grn,fontWeight:700}}>✅ MP Conectado</span>;
  }
  
  return <Btn sz="sm" v="mp" onClick={connectMP}>💸 Conectar Recebedor (MP)</Btn>;
}

function Portaria() {
  const { id } = useParams();
  const { user, login } = useAuth();
  const [evento, setEvento] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    import("./services/firebase").then(({ buscarEvento }) => {
      buscarEvento(id).then(ev => {
        setEvento(ev);
        setLoading(false);
      });
    });
  }, [id]);

  if(loading) return <div style={{height:"100vh",background:"#000",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center"}}>Carregando...</div>;
  if(!evento) return <div style={{height:"100vh",background:"#000",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center"}}>Evento não encontrado.</div>;

  if (!user) {
    return (
      <div style={{height:"100vh",background:"#000",color:"#fff",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:20,textAlign:"center",fontFamily:"'Open Sans', sans-serif"}}>
        <h2 style={{fontFamily:"'Poppins', sans-serif",marginBottom:10}}>Acesso Restrito</h2>
        <p style={{color:"#888",marginBottom:24}}>Você precisa fazer login para acessar a portaria deste evento.</p>
        <button onClick={login} style={{background:K.mp,color:"#fff",border:"none",padding:"14px 28px",borderRadius:99,fontWeight:"bold",fontSize:16,cursor:"pointer",boxShadow:"0 4px 12px rgba(0,158,227,0.3)"}}>Identificar-se com Google</button>
      </div>
    );
  }

  const isAdmin = user.email?.toLowerCase() === "dev.leocarvalho@gmail.com";
  const isOrganizer = evento.organizerId === user.uid || evento.organizerId === user.email;
  const isStaff = evento.staffEmails && Array.isArray(evento.staffEmails) && evento.staffEmails.map(e=>e.toLowerCase()).includes(user.email?.toLowerCase());

  if (!isAdmin && !isOrganizer && !isStaff) {
    return (
      <div style={{height:"100vh",background:"#000",color:"#fff",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:20,textAlign:"center",fontFamily:"'Open Sans', sans-serif"}}>
        <div style={{width:60,height:60,borderRadius:"50%",background:"rgba(255,61,0,0.1)",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:20}}>
          <span style={{fontSize:24}}>🔒</span>
        </div>
        <h2 style={{fontFamily:"'Poppins', sans-serif",color:K.red,marginBottom:10}}>Acesso Negado</h2>
        <p style={{color:"#888",marginBottom:24,maxWidth:300}}>O e-mail <b style={{color:"#fff"}}>{user.email}</b> não está na lista de Equipe de Portaria deste evento.</p>
        <button onClick={() => window.location.href="/"} style={{background:"transparent",border:"1px solid #555",color:"#fff",padding:"12px 24px",borderRadius:99,cursor:"pointer"}}>Voltar ao Início</button>
      </div>
    );
  }

  return (
    <div style={{height:"100vh",background:"#000",position:"relative"}}>
      <Helmet><title>Portaria - {evento.title}</title></Helmet>
      <CheckinScanner expectedEventoId={id} title={evento.title} isStandalone={true} />
    </div>
  );
}

/* ─── COMPONENTE SCANNER ────────────────────────────────────────────────────── */
function CheckinScanner({ onClose, expectedEventoId = null, title = null, isStandalone = false }) {
  const [result, setResult] = useState(null);

  useEffect(() => {
    // Add custom styles for the scanner library
    const style = document.createElement("style");
    style.innerHTML = `
      #reader { border: none !important; }
      #reader img { display: none !important; }
      #reader button { background: #009EE3; color: #fff; border: none; padding: 10px 16px; border-radius: 99px; font-weight: 600; cursor: pointer; margin: 5px; font-family: 'Poppins', sans-serif; }
      #reader select { background: #222; color: #fff; border: 1px solid #444; padding: 8px; border-radius: 8px; outline: none; margin-bottom: 10px; width: 100%; max-width: 300px; }
      #reader__scan_region { min-height: 250px; }
      #reader__dashboard_section_csr span { color: #fff !important; }
    `;
    document.head.appendChild(style);

    const scanner = new Html5QrcodeScanner("reader", { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 }, false);
    
    scanner.render(async (decodedText) => {
      scanner.pause();
      setResult({ status: "loading", msg: "Verificando Ingresso..." });
      try {
        await realizarCheckin(decodedText, expectedEventoId);
        setResult({ status: "success", msg: "✅ Acesso Liberado" });
      } catch (err) {
        setResult({ status: "error", msg: "❌ " + err.message });
      }
      setTimeout(() => {
        setResult(null);
        scanner.resume();
      }, 3500);
    }, (err) => {
      // ignora leituras vazias
    });

    return () => {
      document.head.removeChild(style);
      scanner.clear().catch(console.error);
    };
  }, []);

  return (
    <div className="fi" style={{position: "fixed", inset: 0, zIndex: 99999, background: "#050505", display: "flex", flexDirection: "column", fontFamily: "'Open Sans', sans-serif"}}>
      {/* Header */}
      <div style={{padding: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", background: "linear-gradient(to bottom, rgba(0,0,0,0.8), transparent)", position: "absolute", top: 0, left: 0, right: 0, zIndex: 10}}>
        <div style={{display: "flex", flexDirection: "column"}}>
          <span style={{fontSize: 12, fontWeight: 700, color: K.red, textTransform: "uppercase", letterSpacing: "1px"}}>Modo Leitura</span>
          <h2 style={{fontFamily: "'Poppins', sans-serif", color: "#fff", margin: 0, fontSize: 20}}>{title || "Portaria Digital"}</h2>
        </div>
        {!isStandalone && (
          <button onClick={onClose} style={{background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", width: 40, height: 40, borderRadius: 999, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.25)"} onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.15)"}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"></path></svg>
          </button>
        )}
      </div>

      {/* Main Scanner Area */}
      <div style={{flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", position: "relative", padding: 20}}>
        
        {/* Animated Scanner Wrapper */}
        <div style={{width: "100%", maxWidth: 400, margin: "0 auto", position: "relative"}}>
          <div id="reader" style={{ width: "100%", background: "transparent", borderRadius: 24, overflow: "hidden", color: "#fff" }}></div>
          
          {/* Overlay Corners */}
          <div style={{position: "absolute", top: -10, left: -10, right: -10, bottom: -10, border: `2px solid rgba(255,255,255,0.1)`, borderRadius: 32, pointerEvents: "none"}}>
            <div style={{position: "absolute", top: -2, left: -2, width: 40, height: 40, borderTop: `4px solid ${K.mp}`, borderLeft: `4px solid ${K.mp}`, borderTopLeftRadius: 32}}></div>
            <div style={{position: "absolute", top: -2, right: -2, width: 40, height: 40, borderTop: `4px solid ${K.mp}`, borderRight: `4px solid ${K.mp}`, borderTopRightRadius: 32}}></div>
            <div style={{position: "absolute", bottom: -2, left: -2, width: 40, height: 40, borderBottom: `4px solid ${K.mp}`, borderLeft: `4px solid ${K.mp}`, borderBottomLeftRadius: 32}}></div>
            <div style={{position: "absolute", bottom: -2, right: -2, width: 40, height: 40, borderBottom: `4px solid ${K.mp}`, borderRight: `4px solid ${K.mp}`, borderBottomRightRadius: 32}}></div>
          </div>
        </div>

        {/* Result Overlay */}
        {result && (
          <div className="fu" style={{
            position: "absolute", bottom: 80, left: "50%", transform: "translateX(-50%)",
            background: result.status === "success" ? "#00E676" : result.status === "error" ? "#FF3D00" : "rgba(255,255,255,0.9)",
            color: result.status === "loading" ? "#000" : "#fff",
            padding: "20px 40px", borderRadius: 999, fontSize: 18, fontWeight: 800,
            textAlign: "center", zIndex: 20, boxShadow: "0 10px 40px rgba(0,0,0,0.6)",
            width: "max-content", maxWidth: "90%", display: "flex", alignItems: "center", gap: 12
          }}>
            {result.status === "loading" && <span style={{display: "inline-block", width: 16, height: 16, border: "3px solid #000", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite"}}></span>}
            {result.msg}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{padding: "30px 24px 40px", textAlign: "center", background: "linear-gradient(to top, rgba(0,0,0,0.9), transparent)", position: "absolute", bottom: 0, left: 0, right: 0, pointerEvents: "none"}}>
        <p style={{color: "rgba(255,255,255,0.6)", fontSize: 14, margin: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 8}}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
          Aponte a câmera para o QR Code do cliente.
        </p>
      </div>
    </div>
  );
}

/* ─── Modal de Checkout ─────────────────────────────────────────────────────── */
const STEPS=["Identificação","Resumo","Pagamento","Ingresso"];

function Checkout({evento,onClose,onAuthNeeded}){
  const {user,userData}=useAuth();
  const [step,setStep]=useState(user?1:0);
  const [qty,setQty]=useState(1);
  const [customAmount,setCustomAmount]=useState(50);
  const [loading,setLoading]=useState(false);
  const [ingressoId,setIngressoId]=useState(null);
  const [linkMP,setLinkMP]=useState(null);
  const [ingresso,setIngresso]=useState(null);
  const [errMsg,setErrMsg]=useState("");
  const [payMethod,setPayMethod]=useState("pix_nativo");
  const [hasWarning,setHasWarning]=useState(false);

  useEffect(()=>{
    if(!user)return;
    const unsub=escutarIngressos(user.uid,list=>{
      setHasWarning(list.some(i=>i.eventoId===evento.id && i.status!=="cancelled" && i.status!=="rejected"));
    });
    return unsub;
  },[user, evento.id]);

  // Escuta o ingresso em tempo real → atualiza quando webhook chegar
  useEffect(()=>{
    if(!ingressoId)return;
    const unsub=escutarIngresso(ingressoId,t=>{
      setIngresso(t);
      if(t.status==="confirmed")setStep(3);
    });
    return unsub;
  },[ingressoId]);

  useEffect(()=>{if(user&&step===0)setStep(1);},[user]);

  const basePrice = evento.isCampaign ? Number(customAmount) : evento.price;
  const mpFee = (payMethod==="credito" && basePrice>0) ? basePrice * qty * 0.06 : 0;
  const total = (basePrice * qty) + mpFee;

  const handlePagar=async()=>{
    if(evento.isCampaign && (!customAmount || customAmount < 5)) {
      setErrMsg("O valor mínimo para doação é de R$ 5,00.");
      return;
    }
    setLoading(true);setErrMsg("");
    try{
      const finalQty = evento.isCampaign ? 1 : qty;
      const res=await criarPagamento(evento.id,finalQty,payMethod, evento.isCampaign ? Number(customAmount) : null);
      if(res.gratuito){
        setIngresso(res.ingresso);setIngressoId(res.ingresso.id);setStep(3);
      }else{
        setIngressoId(res.ingressoId);
        setLinkMP(res.sandboxLink||res.link); // sandbox em testes, link em produção
        setIngresso({
          id:res.ingressoId,
          status:"pending",
          qrCodeBase64: res.qrCodeBase64,
          qrCode: res.qrCode,
          eventoTitulo:evento.title,
          eventoData:evento.date,
          eventoHora:evento.time,
          eventoFim:evento.endTime||evento.fim,
          eventoLocal:`${evento.location||evento.local} — ${evento.city}/${evento.state}`,
          nome:userData?.nome||user.displayName||user.email,
          email:user.email,
          color:evento.color,
          emoji:evento.emoji
        });
        setStep(3);
      }
    }catch(err){setErrMsg(err.message||"Erro ao processar pagamento.");}
    finally{setLoading(false);}
  };

  const statusColor={confirmed:K.grn,pending:K.amb,rejected:K.red};
  const statusLabel={confirmed:"✅ Pagamento confirmado",pending:"⏳ Aguardando pagamento",rejected:"❌ Pagamento recusado"};

  return(
    <Modal onClose={step<3?onClose:undefined}>
      <div style={{background:evento.color,borderRadius:`${K.r.xxl}px ${K.r.xxl}px 0 0`,padding:"22px 26px 18px",color:"#fff",position:"relative"}}>
        {step<3&&<button onClick={onClose} style={{position:"absolute",top:18,right:18,background:"rgba(255,255,255,.2)",border:"none",color:"#fff",width:28,height:28,borderRadius:8,cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>}
        <p style={{fontSize:11,letterSpacing:"1.5px",textTransform:"uppercase",opacity:.65,marginBottom:3}}>Inscrição</p>
        <h2 style={{fontFamily:"'Poppins',sans-serif",fontSize:19,lineHeight:1.2,marginBottom:14,wordBreak:"break-word"}}>{evento.title}</h2>
        <div style={{display:"flex",alignItems:"center"}}>
          {STEPS.map((s,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",flex:i<STEPS.length-1?1:undefined}}>
              <div style={{width:24,height:24,borderRadius:999,flexShrink:0,background:step>i?"#fff":step===i?"rgba(255,255,255,.25)":"rgba(255,255,255,.12)",border:step===i?"2px solid #fff":"none",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:step>i?evento.color:"#fff",transition:"all .3s"}}>{step>i?"✓":i+1}</div>
              {i<STEPS.length-1&&<div style={{flex:1,height:1.5,background:step>i?"rgba(255,255,255,.7)":"rgba(255,255,255,.2)",margin:"0 5px",transition:"background .3s"}}/>}
            </div>
          ))}
        </div>
      </div>

      <div className="mob-pd" style={{padding:"22px 26px 26px"}}>
        {/* STEP 0 — não logado */}
        {step===0&&(
          <div className="fu" style={{textAlign:"center",padding:"16px 0"}}>
            <p style={{fontSize:28,marginBottom:12}}>🔐</p>
            <h3 style={{fontFamily:"'Poppins',sans-serif",fontSize:20,color:K.ink,marginBottom:8}}>Identifique-se para continuar</h3>
            <p style={{color:K.mid,fontSize:14,marginBottom:24}}>Faça login ou crie sua conta para garantir seu ingresso.</p>
            <Btn full sz="lg" onClick={onAuthNeeded} sx={{background:evento.color}}>Entrar ou criar conta →</Btn>
          </div>
        )}

        {/* STEP 1 — Resumo */}
        {step===1&&(
          <div className="fu">
            {hasWarning && !evento.isCampaign && (
              <div style={{background:K.ambBg,border:`1px solid ${K.amb}30`,color:K.amb,padding:"10px 14px",borderRadius:K.r.m,marginBottom:16,fontSize:13,fontWeight:600}}>
                ⚠️ Você já possui ingressos para este evento. Deseja comprar mais um?
              </div>
            )}
            <div style={{background:K.alt,borderRadius:K.r.l,padding:18,marginBottom:18}}>
              <p style={{fontSize:11,color:K.lt,textTransform:"uppercase",letterSpacing:"0.4px",marginBottom:3}}>{evento.isCampaign?"Campanha":"Evento"}</p>
              <p style={{fontFamily:"'Poppins',sans-serif",fontSize:18,color:K.ink,marginBottom:5}}>{evento.title}</p>
              {!evento.isCampaign && <p style={{fontSize:13,color:K.mid}}>{fmtD(evento.date)} • {evento.time||evento.time}–{evento.endTime||evento.fim}</p>}
              <p style={{fontSize:13,color:K.mid}}>{evento.location||evento.local}, {evento.city}/{evento.state}</p>
            </div>
            {evento.isCampaign ? (
              <>
                <p style={{fontWeight:700,fontSize:14,color:K.ink,marginBottom:10}}>Qual o valor da sua contribuição?</p>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:16}}>
                  {[5, 10, 25, 50, 100, 150].map(v => (
                    <button key={v} onClick={()=>setCustomAmount(v)} style={{height:46,borderRadius:K.r.m,border:`2px solid ${customAmount===v?K.ink:K.brd}`,background:customAmount===v?K.ink:K.sur,color:customAmount===v?"#fff":K.ink,fontSize:15,fontWeight:600,cursor:"pointer",transition:"all .2s",position:"relative"}}>
                      {v===50 && customAmount!==50 && <span style={{position:"absolute",top:-8,left:"50%",transform:"translateX(-50%)",background:K.grn,color:"#fff",fontSize:9,padding:"2px 6px",borderRadius:4,fontWeight:800}}>SUGESTÃO</span>}
                      R$ {v}
                    </button>
                  ))}
                </div>
                <div style={{marginBottom:22}}>
                  <Inp label="Ou digite um valor livre (R$)" type="number" min="5" step="1" value={customAmount} onChange={e=>setCustomAmount(Number(e.target.value))} placeholder="Ex: 200"/>
                  <p style={{fontSize:11,color:K.mid,marginTop:-6}}>Doação mínima de R$ 5,00.</p>
                </div>
              </>
            ) : (
              <>
                <p style={{fontWeight:700,fontSize:14,color:K.ink,marginBottom:10}}>Quantidade</p>
                <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:22}}>
                  {[-1,null,1].map((d,i)=>d===null?<span key="n" style={{fontFamily:"'Poppins',sans-serif",fontSize:30,fontWeight:700,color:K.ink,minWidth:28,textAlign:"center"}}>{qty}</span>:(
                    <button key={i} onClick={()=>setQty(q=>Math.min(10,Math.max(1,q+d)))} style={{width:40,height:40,borderRadius:K.r.m,border:`1.5px solid ${K.brd}`,background:K.sur,fontSize:20,cursor:"pointer",color:K.ink,display:"flex",alignItems:"center",justifyContent:"center"}}>{d<0?"−":"+"}</button>
                  ))}
                  <span style={{color:K.lt,fontSize:13}}>máx. 10</span>
                </div>
              </>
            )}
            
            <div style={{border:`1.5px solid ${K.brd}`,borderRadius:K.r.l,padding:16,marginBottom:18}}>
              {!evento.isCampaign && evento.price>0 && <div style={{display:"flex",justifyContent:"space-between",fontSize:14,color:K.mid,marginBottom:8}}><span>Subtotal</span><span>{qty}x {fmtP(evento.price)}</span></div>}
              {evento.isCampaign && basePrice>0 && <div style={{display:"flex",justifyContent:"space-between",fontSize:14,color:K.mid,marginBottom:8}}><span>Doação</span><span>{fmtP(basePrice)}</span></div>}
              {basePrice>0&&payMethod==="credito"&&<div style={{display:"flex",justifyContent:"space-between",fontSize:14,color:K.mid,marginBottom:8}}><span>Taxa MP (6%)</span><span>{fmtP(mpFee)}</span></div>}
              <div style={{borderTop:`1px solid ${K.brd}`,paddingTop:10,display:"flex",justifyContent:"space-between"}}>
                <span style={{fontWeight:700,color:K.ink}}>Total</span>
                <span style={{fontFamily:"'Poppins',sans-serif",fontSize:22,fontWeight:700,color:basePrice===0?K.grn:K.ink}}>{basePrice===0?"Gratuito":`R$ ${total.toFixed(2).replace(".",",")}`}</span>
              </div>
            </div>

            {basePrice > 0 && (
              <div style={{marginBottom:22}}>
                <p style={{fontWeight:700,fontSize:14,color:K.ink,marginBottom:10}}>Método de Pagamento</p>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <label style={{display:"flex",alignItems:"center",gap:10,background:K.sur,border:`1.5px solid ${payMethod==="pix_nativo"?K.ink:K.brd}`,padding:14,borderRadius:K.r.m,cursor:"pointer",transition:"all .2s"}}>
                    <input type="radio" name="payMethod" value="pix_nativo" checked={payMethod==="pix_nativo"} onChange={(e)=>setPayMethod(e.target.value)} style={{width:18,height:18,accentColor:K.ink}}/>
                    <div style={{flex:1}}>
                      <p style={{fontSize:15,fontWeight:600,color:K.ink}}>PIX (QR Code Instantâneo)</p>
                      <p style={{fontSize:12,color:K.mid}}>Aprovação na hora. Sem taxas extras.</p>
                    </div>
                    <span style={{fontSize:24}}>💠</span>
                  </label>
                  <label style={{display:"flex",alignItems:"center",gap:10,background:K.sur,border:`1.5px solid ${payMethod==="all"?K.ink:K.brd}`,padding:14,borderRadius:K.r.m,cursor:"pointer",transition:"all .2s"}}>
                    <input type="radio" name="payMethod" value="all" checked={payMethod==="all"} onChange={(e)=>setPayMethod(e.target.value)} style={{width:18,height:18,accentColor:K.ink}}/>
                    <div style={{flex:1}}>
                      <p style={{fontSize:15,fontWeight:600,color:K.ink}}>Cartão de Crédito ou Boleto</p>
                      <p style={{fontSize:12,color:K.mid}}>Ambiente seguro Mercado Pago.</p>
                    </div>
                    <span style={{fontSize:24}}>💳</span>
                  </label>
                </div>
              </div>
            )}
            <Btn full sz="lg" onClick={()=>setStep(2)} sx={{background:evento.color}}>
              {basePrice===0?"Confirmar inscrição →":"Ir para pagamento →"}
            </Btn>
          </div>
        )}

        {/* STEP 2 — Pagamento */}
        {step===2&&(
          <div className="fu">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <div>
                <p style={{fontSize:13,color:K.mid}}>Total</p>
                <p style={{fontFamily:"'Poppins',sans-serif",fontSize:26,fontWeight:700,color:basePrice===0?K.grn:K.ink}}>{basePrice===0?"Gratuito":`R$ ${total.toFixed(2).replace(".",",")}`}</p>
              </div>
            </div>

            {basePrice===0 && (
              <div style={{background:K.grnBg,border:`1px solid ${K.grn}30`,borderRadius:K.r.l,padding:20,textAlign:"center",marginBottom:20}}>
                <p style={{fontSize:28,marginBottom:8}}>🎉</p>
                <p style={{fontWeight:700,color:K.grn}}>Evento gratuito!</p>
              </div>
            )}

            <div style={{background:K.alt,borderRadius:K.r.m,padding:"10px 14px",marginBottom:16,fontSize:13,color:K.mid}}>
              <p>👤 <strong style={{color:K.ink}}>{userData?.nome||user?.displayName||user?.email}</strong> • {user?.email}</p>
            </div>

            {errMsg&&<div style={{background:K.redBg,border:`1px solid ${K.red}30`,borderRadius:K.r.m,padding:"10px 14px",marginBottom:14,fontSize:13,color:K.red}}>⚠️ {errMsg}</div>}

            <Btn full sz="lg" loading={loading} onClick={handlePagar} v={basePrice===0?"grn":"mp"}>
              {basePrice===0?"Confirmar inscrição gratuita ✓":"Pagar com Mercado Pago →"}
            </Btn>
            <p style={{textAlign:"center",fontSize:12,color:K.lt,marginTop:10}}>🔒 Pagamento processado com segurança pelo Mercado Pago</p>
          </div>
        )}

        {/* STEP 3 — Ingresso */}
        {step===3&&ingresso&&(
          <div className="fu" style={{textAlign:"center"}}>
            <div style={{fontSize:48,marginBottom:10}}>{ingresso.status==="confirmed"?"🎉":"🔗"}</div>
            <h3 style={{fontFamily:"'Poppins',sans-serif",fontSize:22,color:K.ink,marginBottom:6}}>
              {ingresso.status==="confirmed"?"Ingresso confirmado!":ingresso.status==="gratuito"||ingresso.total===0?"Inscrição confirmada!":"Link de pagamento gerado!"}
            </h3>
            <p style={{color:K.mid,fontSize:14,marginBottom:20}}>{ingresso.email}</p>

            {/* Ingresso visual (apenas se pago/gratuito) */}
            {(ingresso.status==="confirmed" || ingresso.status==="gratuito") && (
              <div style={{background:`linear-gradient(145deg,${evento.color} 0%,${evento.color}bb 100%)`,borderRadius:K.r.xxl,padding:"20px 20px 0",color:"#fff",marginBottom:16,textAlign:"left",overflow:"hidden",position:"relative"}}>
                <div style={{position:"absolute",top:-25,right:-25,width:80,height:80,borderRadius:999,background:"rgba(255,255,255,.06)"}}/>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12,position:"relative"}}>
                  <div>
                    <p style={{fontSize:9,opacity:.55,letterSpacing:"1.5px",textTransform:"uppercase",marginBottom:3}}>Eventos&Encontros • Ingresso Digital</p>
                    <h3 style={{fontFamily:"'Poppins',sans-serif",fontSize:16,lineHeight:1.2}}>{ingresso.eventoTitulo}</h3>
                  </div>
                  <span style={{fontSize:32}}>{ingresso.emoji||evento.emoji}</span>
                </div>
                <div style={{display:"flex",justifyContent:"center",margin:"0 0 12px"}}>
                  <div style={{background:"#fff",borderRadius:K.r.l,padding:10,boxShadow:"0 4px 20px rgba(0,0,0,.2)"}}>
                    <img src={getQR(`EVENTOSEENCONTROS|ID:${ingresso.id}|EVENTO:${ingresso.eventoTitulo}|NOME:${ingresso.nome}|EMAIL:${ingresso.email}`)} width={140} height={140} alt="QR" style={{display:"block",borderRadius:6}}/>
                  </div>
                </div>
                <p style={{textAlign:"center",fontFamily:"monospace",fontSize:12,opacity:.65,letterSpacing:"1.5px",marginBottom:12}}>{ingresso.id}</p>
                <div style={{margin:"0 -20px",borderTop:"2px dashed rgba(255,255,255,.25)",paddingTop:12,paddingBottom:16,paddingLeft:20,paddingRight:20,position:"relative"}}>
                  <div style={{position:"absolute",left:-7,top:-7,width:14,height:14,borderRadius:999,background:K.sur}}/>
                  <div style={{position:"absolute",right:-7,top:-7,width:14,height:14,borderRadius:999,background:K.sur}}/>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px 4px"}}>
                    {[
                      ["Participante",ingresso.nome],
                      ingresso.eventoData && ["Data",fmtD(ingresso.eventoData)?.slice(0,20)||ingresso.eventoData],
                      ingresso.eventoHora && ["Horário",`${ingresso.eventoHora}–${ingresso.eventoFim||""}`],
                      ["Local",ingresso.eventoLocal]
                    ].filter(Boolean).map(([k,v])=>(
                      <div key={k}><p style={{fontSize:9,opacity:.5,textTransform:"uppercase",letterSpacing:"0.8px",marginBottom:1}}>{k}</p><p style={{fontSize:11,fontWeight:600,lineHeight:1.3}}>{v}</p></div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Status em tempo real */}
            <div style={{background:ingresso.status==="confirmed"?K.grnBg:K.ambBg,border:`1px solid ${(ingresso.status==="confirmed"?K.grn:K.amb)}30`,borderRadius:K.r.m,padding:"11px 14px",marginBottom:16,display:"flex",gap:8,alignItems:"center"}}>
              <span style={{animation:ingresso.status==="pending"?"pulse 1.5s infinite":"none"}}>{ingresso.status==="confirmed"?"✅":"⏳"}</span>
              <div style={{textAlign:"left"}}>
                <p style={{fontWeight:700,fontSize:13,color:ingresso.status==="confirmed"?K.grn:K.amb}}>{statusLabel[ingresso.status]||"Aguardando..."}</p>
                {ingresso.status==="pending"&&<p style={{fontSize:12,color:K.mid,marginTop:2}}>O ingresso será confirmado automaticamente após o pagamento. Esta página atualiza sozinha.</p>}
              </div>
            </div>

            {/* Link do MP ou QR Code */}
            {ingresso.qrCodeBase64 && ingresso.status!=="confirmed" && (
              <div style={{textAlign:"center", padding:"10px 0", marginBottom: 20}}>
                <p style={{fontSize:13, color:K.mid, marginBottom:12}}>Escaneie o QR Code abaixo no seu app de banco para pagar via PIX:</p>
                <img src={`data:image/jpeg;base64,${ingresso.qrCodeBase64}`} alt="QR Code PIX" style={{width: 200, height: 200, borderRadius: 8, border:`1px solid ${K.brd}`}} />
                <div style={{marginTop: 14}}>
                  <Btn sz="md" onClick={() => {
                    navigator.clipboard.writeText(ingresso.qrCode);
                    alert("Código PIX copiado!");
                  }}>Copiar Código PIX (Copia e Cola)</Btn>
                </div>
              </div>
            )}

            {linkMP && !ingresso.qrCodeBase64 && ingresso.status!=="confirmed"&&(
              <Btn full v="mp" sz="md" onClick={()=>window.open(linkMP,"_blank")} sx={{marginBottom:10}}>
                Ir para o Checkout Mercado Pago →
              </Btn>
            )}

            <div style={{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap"}}>
              <Btn sz="sm" sx={{background:"#25D366",color:"#fff"}} onClick={()=>window.open(`https://wa.me/?text=${encodeURIComponent(window.location.href)}`)}>📱 WhatsApp</Btn>
              <Btn sz="sm" v="out" onClick={() => {
                if (navigator.share) {
                  navigator.share({ url: window.location.href }).catch(()=>{});
                } else {
                  navigator.clipboard.writeText(window.location.href);
                  alert("Link copiado para a área de transferência!");
                }
              }}>🔗 Compartilhar</Btn>
              <Btn sz="sm" v="out" onClick={onClose}>Fechar</Btn>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ─── Chatbox (Fale Conosco) ────────────────────────────────────────────────── */
function Chatbox() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    // DEVSECOPS FIX: Nunca exponha e-mail em texto puro no frontend.
    // Acesse o site do formsubmit.co, insira seu e-mail, valide-o e pegue a string aleatória fornecida por eles.
    const formSubmitEndpoint = "https://formsubmit.co/ajax/SEU_ALIAS_DO_FORMSUBMIT_AQUI"; 
    
    if (formSubmitEndpoint.includes("SEU_ALIAS")) {
      alert("⚠️ Aviso de Segurança (Admin): Você precisa configurar o FormSubmit com uma string anônima. Leia o código em App.js linha ~670.");
      return;
    }

    setStatus("sending");
    const formData = new FormData(e.target);
    try {
      await fetch(formSubmitEndpoint, {
        method: "POST",
        headers: { "Accept": "application/json" },
        body: formData
      });
      setStatus("success");
      setTimeout(() => {
        setOpen(false);
        setStatus("");
      }, 3000);
    } catch (error) {
      setStatus("error");
    }
  };

  return (
    <>
      {/* Botão flutuante */}
      <button 
        onClick={() => setOpen(!open)}
        className="hov"
        style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 9000,
          width: 56, height: 56, borderRadius: 999, background: K.ink, color: "#fff",
          border: "none", boxShadow: K.sh.l, fontSize: 24, display: "flex", 
          alignItems: "center", justifyContent: "center", cursor: "pointer",
          transition: "transform 0.2s"
        }}
      >
        {open ? "✕" : "💬"}
      </button>

      {/* Janela do Chat */}
      {open && (
        <div className="fu" style={{
          position: "fixed", bottom: 90, right: 24, zIndex: 9000,
          width: "calc(100vw - 48px)", maxWidth: 340, background: K.sur,
          borderRadius: K.r.xl, boxShadow: K.sh.xl, border: `1px solid ${K.brd}`,
          overflow: "hidden", display: "flex", flexDirection: "column"
        }}>
          <div style={{background: K.ink, color: "#fff", padding: "16px 20px"}}>
            <h3 style={{fontFamily: "'Poppins',sans-serif", fontSize: 16, margin: 0}}>Fale Conosco</h3>
            <p style={{fontSize: 12, opacity: 0.8, margin: 0}}>Envie sua dúvida e responderemos no seu e-mail.</p>
          </div>
          
          <div style={{padding: 20}}>
            {status === "success" ? (
              <div style={{textAlign: "center", padding: "20px 0", color: K.grn}}>
                <span style={{fontSize: 32}}>✅</span>
                <p style={{fontWeight: 600, marginTop: 8}}>Mensagem enviada com sucesso!</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{display: "flex", flexDirection: "column", gap: 12}}>
                {/* Anti-spam do formsubmit */}
                <input type="hidden" name="_captcha" value="false" />
                <input type="hidden" name="_subject" value="Novo contato pelo Chatbox do Eventos e Encontros" />
                
                <input required type="text" name="name" placeholder="Seu nome" style={{width: "100%", padding: "10px 14px", border: `1px solid ${K.brd}`, borderRadius: K.r.s, fontSize: 14}} />
                <input required type="email" name="email" placeholder="Seu e-mail" style={{width: "100%", padding: "10px 14px", border: `1px solid ${K.brd}`, borderRadius: K.r.s, fontSize: 14}} />
                <textarea required name="message" placeholder="Como podemos ajudar?" rows={4} style={{width: "100%", padding: "10px 14px", border: `1px solid ${K.brd}`, borderRadius: K.r.s, fontSize: 14, resize: "none", fontFamily: "inherit"}}></textarea>
                
                {status === "error" && <p style={{color: K.red, fontSize: 12, margin: 0}}>Erro ao enviar. Tente novamente.</p>}
                
                <Btn full sz="md" loading={status === "sending"} type="submit" sx={{marginTop: 4}}>
                  Enviar Mensagem
                </Btn>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* ─── Event Card ────────────────────────────────────────────────────────────── */
function EventCard({ev,onClick}){
  const pct=Math.round(((ev.spots-ev.spotsLeft)/ev.spots)*100)||0;
  const urg=(ev.spotsLeft||0)<50;
  const isPast = ev.date ? new Date(ev.date + "T23:59:59") < new Date() : false;
  const isClosed = ev.registrationClosed || isPast;
  const hasImg = isValidImage(ev.image);
  return(
    <div className="hov" onClick={isClosed?undefined:onClick} style={{background:K.sur,borderRadius:K.r.xl,border:`1px solid ${K.brd}`,boxShadow:K.sh.s,overflow:"hidden",opacity:isClosed?0.6:1,filter:isClosed?"grayscale(60%)":"none"}}>
      <div style={{height:148,background:hasImg?`url(${ev.image}) center/cover no-repeat`:`linear-gradient(135deg,${ev.color}f0,${ev.color}80)`,display:"flex",alignItems:"center",justifyContent:"center",position:"relative"}}>
        {!hasImg&&<span style={{fontSize:58}}>{ev.emoji}</span>}
        <div style={{position:"absolute",top:10,left:10}}><Chip c="#fff" bg="rgba(0,0,0,.28)">{ev.category||ev.cat}</Chip></div>
        {ev.price===0 && !ev.isCampaign && <div style={{position:"absolute",top:10,right:10}}><Chip c={K.grn} bg={K.grnBg}>Gratuito</Chip></div>}
        {ev.featured&&ev.price>0&&<div style={{position:"absolute",top:10,right:10}}><Chip c={K.amb} bg={K.ambBg}>⭐ Destaque</Chip></div>}
      </div>
      <div style={{padding:"16px 16px 14px"}}>
        {ev.isCampaign ? (
          <p style={{fontSize:11,color:K.lt,marginBottom:4,fontWeight:500}}>{ev.city}, {ev.state}</p>
        ) : (
          <p style={{fontSize:11,color:K.lt,marginBottom:4,fontWeight:500}}>{fmtD(ev.date)?.split(",")[0]+"," } {ev.city}, {ev.state}</p>
        )}
        <h3 style={{fontFamily:"'Poppins',sans-serif",fontSize:17,color:K.ink,lineHeight:1.25,marginBottom:7}}>{ev.title}</h3>
        <p style={{fontSize:13,color:K.mid,lineHeight:1.5,marginBottom:10,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{ev.description||ev.desc}</p>
        <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:10}}>
          {(ev.tags||[]).map(t=><Chip key={t}>{t}</Chip>)}
        </div>
        {!ev.isCampaign && (
          <>
            <div style={{background:K.alt,borderRadius:999,height:4,overflow:"hidden",marginBottom:4}}>
              <div style={{width:`${pct}%`,height:"100%",background:urg?K.red:ev.color,borderRadius:999}}/>
            </div>
            <p style={{fontSize:11,color:urg?K.red:K.lt,marginBottom:12,fontWeight:urg?700:400}}>{urg?`⚡ Apenas ${ev.spotsLeft} vagas!`:`${ev.spotsLeft||0} vagas disponíveis`}</p>
          </>
        )}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontFamily:"'Poppins',sans-serif",fontSize:19,fontWeight:700,color:ev.price===0?K.grn:K.ink}}>{ev.isCampaign ? "Doação Aberta" : fmtP(ev.price)}</span>
          <div style={{display:"flex", gap: 8}}>
            <Btn sz="sm" v="out" onClick={(e) => {
              e.stopPropagation();
              const url = `${window.location.origin}/evento/${ev.id}`;
              if (navigator.share) {
                navigator.share({ url }).catch(()=>{});
              } else {
                navigator.clipboard.writeText(url);
                alert("Link copiado para a área de transferência!");
              }
            }}>🔗 Compartilhar</Btn>
            {isClosed ? (
              <Btn sz="sm" sx={{background:K.mid, cursor: "not-allowed"}} disabled>Encerrado</Btn>
            ) : (
              <Btn sz="sm" sx={{background:ev.color}}>Ver →</Btn>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Meus Ingressos ────────────────────────────────────────────────────────── */
function MeusIngressos({onClose}){
  const {user}=useAuth();
  const [ingressos,setIngressos]=useState([]);
  const [loading,setLoading]=useState(true);

  const [deletandoId, setDeletandoId]=useState(null);

  useEffect(()=>{
    if(!user)return;
    const unsub=escutarIngressos(user.uid,t=>{setIngressos(t);setLoading(false);});
    return unsub;
  },[user]);

  const handleDelete = async (id) => {
    if(!window.confirm("Deseja realmente cancelar este pedido pendente?")) return;
    setDeletandoId(id);
    try { await excluirIngresso(id); }
    catch(e) { alert("Erro ao cancelar: " + e.message); }
    setDeletandoId(null);
  };

  const statusStyle={
    confirmed:{label:"✅ Confirmado",c:K.grn,bg:K.grnBg},
    pending:{label:"⏳ Aguardando Pagamento",c:K.amb,bg:K.ambBg},
    rejected:{label:"❌ Recusado",c:K.red,bg:K.redBg},
  };

  const pendentes = ingressos.filter(i => i.status === "pending");
  const concluidos = ingressos.filter(i => i.status !== "pending");

  const renderTicket = (t) => {
    const st=statusStyle[t.status]||statusStyle.pending;
    return (
      <div key={t.id} style={{background:K.sur, border:`1px solid ${K.brd}`, borderRadius:16, marginBottom:20, boxShadow:"0 10px 30px rgba(0,0,0,0.05)", position:"relative", overflow:"hidden", opacity: t.status==="rejected"?0.6:1}}>
        <div style={{height:8, background: t.eventoColor || K.ink}} />
        <div className="mob-pd" style={{padding:"20px 24px"}}>
          <div className="mob-col" style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16, gap:12}}>
            <div style={{minWidth:0}}>
              <p style={{fontSize:11, color:K.lt, textTransform:"uppercase", letterSpacing:"1px", marginBottom:4}}>Ingresso {t.quantidade>1?`(${t.quantidade}x)`:""}</p>
              <h4 style={{fontFamily:"'Poppins',sans-serif", fontSize:20, color:K.ink, lineHeight:1.2, wordBreak:"break-word"}}>{t.eventoTitulo}</h4>
            </div>
            <Chip c={st.c} bg={st.bg} sx={{flexShrink:0, whiteSpace:"nowrap"}}>{st.label}</Chip>
          </div>

          <div className="mob-col" style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:20, paddingBottom:20, borderBottom:`2px dashed ${K.alt}`}}>
            {t.eventoData && (
              <div>
                <p style={{fontSize:11, color:K.lt, textTransform:"uppercase"}}>Data e Hora</p>
                <p style={{fontSize:14, fontWeight:600, color:K.ink}}>{t.eventoData} às {t.eventoHora}</p>
              </div>
            )}
            {t.eventoLocal && (
              <div>
                <p style={{fontSize:11, color:K.lt, textTransform:"uppercase"}}>Local</p>
                <p style={{fontSize:14, fontWeight:600, color:K.ink}}>{t.eventoLocal?.split("—")[0]}</p>
              </div>
            )}
            <div>
              <p style={{fontSize:11, color:K.lt, textTransform:"uppercase"}}>Código</p>
              <p style={{fontSize:14, fontWeight:600, color:K.ink, fontFamily:"monospace"}}>{t.id}</p>
            </div>
          </div>

          {t.status === "confirmed" && (
            <div style={{textAlign:"center", padding:"10px 0"}}>
              <p style={{fontSize:12, color:K.mid, marginBottom:12}}>Apresente este QR Code na portaria</p>
              <QRCodeSVG value={t.id} size={160} fgColor={K.ink} bgColor="transparent" />
            </div>
          )}
          
          {t.status === "pending" && t.linkPagamento && (
            <div className="mob-col" style={{display:"flex", gap:8, marginTop:10}}>
              <Btn full v="mp" sz="md" onClick={()=>window.open(t.linkPagamento,"_blank")}>🛒 Concluir Pagamento</Btn>
              <Btn full sz="md" v="out" loading={deletandoId===t.id} onClick={()=>handleDelete(t.id)} sx={{color:K.red, borderColor:K.red}}>Cancelar Inscrição</Btn>
            </div>
          )}
        </div>
      </div>
    );
  };

  return(
    <Modal onClose={onClose} mw={560}>
      <div className="mob-pd" style={{padding:28}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22}}>
          <h2 style={{fontFamily:"'Poppins',sans-serif",fontSize:22,color:K.ink}}>Meus Ingressos</h2>
          <button onClick={onClose} style={{background:K.alt,border:"none",width:28,height:28,borderRadius:8,cursor:"pointer",color:K.mid,fontSize:14}}>✕</button>
        </div>
        {loading?<Spinner/>:ingressos.length===0?(
          <div style={{textAlign:"center",padding:40,color:K.lt}}>
            <p style={{fontSize:36}}>🎫</p>
            <p style={{marginTop:12}}>Você ainda não tem ingressos.</p>
          </div>
        ):(
          <div>
            {pendentes.length > 0 && (
              <div style={{marginBottom: 24}}>
                <h3 style={{fontSize:14, fontWeight:700, color:K.amb, textTransform:"uppercase", marginBottom:12}}>⚠️ Pagamento Pendente</h3>
                {pendentes.map(renderTicket)}
              </div>
            )}
            
            {concluidos.length > 0 && (
              <div>
                <h3 style={{fontSize:14, fontWeight:700, color:K.ink, textTransform:"uppercase", marginBottom:12}}>🎫 Ingressos Registrados</h3>
                {concluidos.map(renderTicket)}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ─── Carrossel de Imagens (reutilizável) ──────────────────────────────────── */
export function ImageCarousel({images=[], height=320, autoPlay=true, interval=4000, borderRadius=K.r.xl}) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const validImgs = images.filter(u => typeof u === "string" && u.startsWith("http"));
  
  useEffect(() => {
    if (!autoPlay || paused || validImgs.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % validImgs.length), interval);
    return () => clearInterval(t);
  }, [autoPlay, paused, validImgs.length, interval]);

  if (validImgs.length === 0) return null;

  return (
    <div style={{position:"relative", width:"100%", height, borderRadius, overflow:"hidden", background:"#000"}}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      {validImgs.map((src, i) => (
        <img key={i} src={src} alt={`Slide ${i+1}`} loading="lazy"
          style={{position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover",
            opacity: i === idx ? 1 : 0, transition:"opacity 0.8s ease-in-out"}} />
      ))}
      {/* Gradient overlay */}
      <div style={{position:"absolute",bottom:0,left:0,right:0,height:"40%",background:"linear-gradient(to top, rgba(0,0,0,0.5), transparent)",pointerEvents:"none"}} />
      {/* Dots */}
      {validImgs.length > 1 && (
        <div style={{position:"absolute",bottom:14,left:"50%",transform:"translateX(-50%)",display:"flex",gap:6,zIndex:5}}>
          {validImgs.map((_,i) => (
            <button key={i} onClick={() => setIdx(i)}
              style={{width: i===idx?24:8, height:8, borderRadius:99, border:"none",
                background: i===idx?"#fff":"rgba(255,255,255,0.45)", cursor:"pointer",
                transition:"all 0.3s"}} />
          ))}
        </div>
      )}
      {/* Arrows */}
      {validImgs.length > 1 && (
        <>
          <button onClick={() => setIdx((idx - 1 + validImgs.length) % validImgs.length)}
            style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",background:"rgba(0,0,0,0.4)",
              backdropFilter:"blur(4px)",border:"none",color:"#fff",width:40,height:40,borderRadius:999,
              cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",
              opacity:0.7,transition:"opacity 0.2s",zIndex:5}}
            onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.7"}>‹</button>
          <button onClick={() => setIdx((idx + 1) % validImgs.length)}
            style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"rgba(0,0,0,0.4)",
              backdropFilter:"blur(4px)",border:"none",color:"#fff",width:40,height:40,borderRadius:999,
              cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",
              opacity:0.7,transition:"opacity 0.2s",zIndex:5}}
            onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.7"}>›</button>
        </>
      )}
      {/* Counter */}
      <span style={{position:"absolute",top:14,right:14,background:"rgba(0,0,0,0.5)",backdropFilter:"blur(4px)",
        color:"#fff",fontSize:12,fontWeight:700,padding:"4px 10px",borderRadius:99,zIndex:5}}>
        {idx+1} / {validImgs.length}
      </span>
    </div>
  );
}

/* ─── Painel do Organizador (Sidebar Profissional) ─────────────────────────── */
function Painel({eventos,onClose,onCriado}){
  const {user}=useAuth();
  const [selectedEvId, setSelectedEvId]=useState(null);
  const [tab,setTab]=useState("overview");
  const [sidebarOpen, setSidebarOpen]=useState(window.innerWidth > 768);
  
  const [f,setF]=useState({title:"",description:"",category:"Outro",emoji:"🎪",color:"#111111",date:"",time:"09:00",endTime:"18:00",location:"",city:"",state:"",price:0,spots:200,image:"",gallery:"",platformFee:7.5,staffEmails:"",isCampaign:false,mpAccessToken:"",webhookUrl:""});
  const [isCreating, setIsCreating] = useState(false);
  const [loading,setLoading]=useState(false);
  const [errMsg,setErrMsg]=useState("");

  const meusEventos = eventos.filter(e => e.organizerId === user?.uid || e.organizerId === user?.email);
  const ev = meusEventos.find(e => e.id === selectedEvId);

  const totalRec = meusEventos.reduce((s,e)=>s+((e.sold||0)*e.price),0);
  const totalInsc = meusEventos.reduce((s,e)=>s+(e.sold||0),0);

  // Close sidebar on mobile when navigating
  const navTo = (newTab, evId=null) => {
    setTab(newTab);
    if (evId !== null) setSelectedEvId(evId);
    if (window.innerWidth <= 768) setSidebarOpen(false);
  };

  const publicar=async()=>{
    if(!f.title||(!f.isCampaign && !f.date)||!f.city){setErrMsg("Preencha nome, data (se não for campanha) e cidade.");return;}
    setLoading(true);setErrMsg("");
    try{
      const processedStaffEmails = f.staffEmails ? f.staffEmails.split(/[\n,]+/).map(e=>e.trim().toLowerCase()).filter(e=>e) : [];
      if (f.id) {
        await atualizarEvento(f.id, {...f,price:Number(f.price),spots:f.isCampaign?999999:Number(f.spots),platformFee:Number(f.platformFee), staffEmails: processedStaffEmails, mpAccessToken: f.mpAccessToken, webhookUrl: f.webhookUrl});
        alert("Evento atualizado com sucesso!");
      } else {
        const res=await criarEvento({...f,price:Number(f.price),spots:f.isCampaign?999999:Number(f.spots),platformFee:Number(f.platformFee), staffEmails: processedStaffEmails, mpAccessToken: f.mpAccessToken, webhookUrl: f.webhookUrl});
        onCriado(res.id);
        alert("Evento criado com sucesso!");
      }
      setIsCreating(false);
      navTo("eventos");
      setF({id:null,title:"",description:"",category:"Outro",emoji:"🎪",color:"#111111",date:"",time:"09:00",endTime:"18:00",location:"",city:"",state:"",price:0,spots:200,image:"",gallery:"",platformFee:7.5,staffEmails:"",isCampaign:false,mpAccessToken:"",webhookUrl:""});
    }catch(err){setErrMsg(err.message||"Erro ao salvar evento.");}
    finally{setLoading(false);}
  };

  const handleEdit = (evData) => {
    setF({
      id: evData.id,
      title: evData.title, description: evData.description || evData.desc || "", category: evData.category || evData.cat || "Outro",
      emoji: evData.emoji || "🎪", color: evData.color || "#111111", date: evData.date || "", time: evData.time || "09:00",
      endTime: evData.endTime || evData.fim || "18:00", location: evData.location || evData.local || "", city: evData.city || "",
      state: evData.state || "", price: evData.price || 0, spots: evData.spots || 200, image: evData.image || "", gallery: evData.gallery || "", platformFee: evData.platformFee !== undefined ? evData.platformFee : 7.5,
      staffEmails: evData.staffEmails ? evData.staffEmails.join("\n") : "",
      isCampaign: evData.isCampaign || false,
      mpAccessToken: evData.mpAccessToken || "",
      webhookUrl: evData.webhookUrl || ""
    });
    navTo("edit");
  };

  const handleDelete = async (evData) => {
    if(!window.confirm(`Tem certeza que deseja excluir o evento "${evData.title}"?`)) return;
    try {
      setLoading(true);
      await excluirEvento(evData.id);
      alert("Evento excluído com sucesso.");
      setSelectedEvId(null);
      navTo("eventos");
    } catch(err) {
      alert("Erro ao excluir: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Sidebar menu item component
  const SideItem = ({icon, label, active, onClick, indent=false, badge=null}) => (
    <button onClick={onClick} style={{
      display:"flex", alignItems:"center", gap:11, width:"100%", padding: indent?"10px 20px 10px 44px":"12px 20px",
      background: active ? "rgba(255,255,255,0.06)" : "transparent",
      borderTop:"none",borderRight:"none",borderBottom:"none",
      borderLeft: active ? "3px solid #009EE3" : "3px solid transparent",
      color: active ? "#fff" : "rgba(255,255,255,0.5)",
      fontSize: indent?13:14, fontWeight: active ? 600 : 400,
      cursor:"pointer", transition:"all 0.15s", textAlign:"left", fontFamily:"inherit",
      letterSpacing:"0.2px"
    }}
      onMouseEnter={e=>{if(!active)e.currentTarget.style.background="rgba(255,255,255,0.03)"}}
      onMouseLeave={e=>{if(!active)e.currentTarget.style.background="transparent"}}>
      <span style={{fontSize: indent?15:18, width:22, textAlign:"center", flexShrink:0}}>{icon}</span>
      <span style={{flex:1}}>{label}</span>
      {badge && <span style={{background:"#009EE3",color:"#fff",fontSize:10,fontWeight:800,padding:"2px 7px",borderRadius:99}}>{badge}</span>}
    </button>
  );

  const breadcrumb = tab === "overview" ? "Dashboard Geral" 
    : tab === "eventos" ? "Meus Eventos" 
    : tab === "criar" ? "Criar Novo Evento"
    : tab === "dash" && ev ? "Dashboard"
    : tab === "parts" ? "Participantes"
    : tab === "mp" ? "Mercado Pago"
    : tab === "portaria" ? "Portaria"
    : tab === "edit" ? "Editar Evento"
    : "Painel";

  const renderForm = () => (
    <div style={{background:K.sur,borderRadius:K.r.xxl,padding:28,maxWidth:640,boxShadow:K.sh.s,border:`1px solid ${K.brd}`}}>
      <h2 style={{fontFamily:"'Poppins',sans-serif",fontSize:22,color:K.ink,marginBottom:22}}>{f.id ? "✏️ Editar evento" : "🚀 Criar novo evento"}</h2>
      <label style={{display:"flex",alignItems:"center",gap:8,marginBottom:18,cursor:"pointer"}}>
        <input type="checkbox" checked={f.isCampaign} onChange={e=>setF({...f,isCampaign:e.target.checked})} style={{width:18,height:18,accentColor:K.pri}}/>
        <span style={{fontSize:14,fontWeight:600,color:K.ink}}>💖 Este é um evento permanente de Doação / Campanha</span>
      </label>
      <Inp label="Nome *" value={f.title} onChange={e=>setF({...f,title:e.target.value})} placeholder="Nome do evento"/>
      <div style={{marginBottom:14}}>
        <label style={{display:"block",fontSize:12,fontWeight:700,color:K.mid,marginBottom:5,textTransform:"uppercase"}}>Descrição</label>
        <textarea value={f.description} onChange={e=>setF({...f,description:e.target.value})} placeholder="Descreva o evento..." style={{width:"100%",border:`1.5px solid ${K.brd}`,borderRadius:K.r.m,padding:"12px 14px",fontSize:15,color:K.ink,background:K.sur,minHeight:80,resize:"vertical",fontFamily:"inherit"}}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <div style={{marginBottom:14}}>
          <label style={{display:"block",fontSize:12,fontWeight:700,color:K.mid,marginBottom:5,textTransform:"uppercase"}}>Categoria</label>
          <select value={f.category} onChange={e=>setF({...f,category:e.target.value})} style={{width:"100%",border:`1.5px solid ${K.brd}`,borderRadius:K.r.m,padding:"12px 14px",fontSize:15,color:K.ink,background:K.sur}}>
            {["Religioso","Tecnologia","Música","Esporte","Gastronomia","Educação","Arte","Negócios","Outro"].map(c=><option key={c}>{c}</option>)}
          </select>
        </div>
        <div style={{marginBottom:14}}>
          <label style={{display:"block",fontSize:12,fontWeight:700,color:K.mid,marginBottom:5,textTransform:"uppercase"}}>Cor</label>
          <input type="color" value={f.color} onChange={e=>setF({...f,color:e.target.value})} style={{width:"100%",height:46,border:`1.5px solid ${K.brd}`,borderRadius:K.r.m,cursor:"pointer",padding:4}}/>
        </div>
      </div>
      {!f.isCampaign && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
          <Inp label="Data *" type="date" value={f.date} onChange={e=>setF({...f,date:e.target.value})}/>
          <Inp label="Início" type="time" value={f.time} onChange={e=>setF({...f,time:e.target.value})}/>
          <Inp label="Fim" type="time" value={f.endTime} onChange={e=>setF({...f,endTime:e.target.value})}/>
        </div>
      )}
      <Inp label="Local" value={f.location} onChange={e=>setF({...f,location:e.target.value})} placeholder="Nome do local"/>
      <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:12}}>
        <Inp label="Cidade *" value={f.city} onChange={e=>setF({...f,city:e.target.value})} placeholder="Cidade"/>
        <Inp label="UF" value={f.state} maxLength={2} onChange={e=>setF({...f,state:e.target.value.toUpperCase()})} placeholder="BA"/>
      </div>
      <Inp label="URL da Imagem de Capa (Opcional)" value={f.image} onChange={e=>setF({...f,image:e.target.value})} placeholder="https://exemplo.com/imagem.jpg"/>
      <div style={{marginBottom:14}}>
        <label style={{display:"block",fontSize:12,fontWeight:700,color:K.mid,marginBottom:5,textTransform:"uppercase"}}>Galeria de Fotos (Mural Pós-Evento)</label>
        <textarea value={f.gallery||""} onChange={e=>setF({...f,gallery:e.target.value})} placeholder={"Cole aqui os links das fotos (um link por linha). Ex:\nhttps://i.imgur.com/foto1.jpg\nhttps://i.imgur.com/foto2.jpg"} style={{width:"100%",border:`1.5px solid ${K.brd}`,borderRadius:K.r.m,padding:"12px 14px",fontSize:14,color:K.ink,background:K.sur,minHeight:80,resize:"vertical",fontFamily:"inherit",lineHeight:1.5}}/>
      </div>
      {!f.isCampaign && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <Inp label="Preço (0=grátis)" type="number" value={f.price} onChange={e=>setF({...f,price:+e.target.value})}/>
          <Inp label="Vagas" type="number" value={f.spots} onChange={e=>setF({...f,spots:+e.target.value})}/>
        </div>
      )}
      <div style={{marginBottom:18}}>
        <label style={{display:"block",fontSize:12,fontWeight:700,color:K.mid,marginBottom:7,textTransform:"uppercase"}}>Ícone</label>
        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
          {["🎪","✝️","🚀","💻","🎶","🏅","🍽️","🎨","📚","🎷","🌟","🎭","🏋️","🎬","🧪","🎓"].map(e=>(
            <button key={e} onClick={()=>setF({...f,emoji:e})} style={{width:40,height:40,borderRadius:K.r.m,border:`2px solid ${f.emoji===e?K.ink:K.brd}`,background:f.emoji===e?K.alt:K.sur,fontSize:18,cursor:"pointer"}}>{e}</button>
          ))}
        </div>
      </div>
      <div style={{background:K.bluBg, border:`1px solid ${K.blu}30`, borderRadius:K.r.m, padding:16, marginBottom:22}}>
        <h3 style={{fontSize:14, fontWeight:700, color:K.blu, marginBottom:8}}>👮 Equipe de Portaria (Staff)</h3>
        <p style={{fontSize:12, color:K.blu, marginTop:-4, marginBottom:10}}>Para permitir que outras pessoas acessem o leitor de QR Code, digite um e-mail por linha. Elas terão acesso apenas à câmera, sem ver dados financeiros.</p>
        <textarea value={f.staffEmails||""} onChange={e=>setF({...f,staffEmails:e.target.value})} placeholder={"exemplo1@gmail.com\nexemplo2@gmail.com"} style={{width:"100%",border:`1.5px solid ${K.blu}50`,borderRadius:K.r.m,padding:"12px 14px",fontSize:14,color:K.ink,background:K.sur,minHeight:100,resize:"vertical",fontFamily:"inherit",lineHeight:1.5}}/>
      </div>
      {user?.email && btoa(user.email.toLowerCase()) === "ZGV2Lmxlb2NhcnZhbGhvQGdtYWlsLmNvbQ==" && (
        <div style={{background:K.ambBg, border:`1px solid ${K.amb}30`, borderRadius:K.r.m, padding:16, marginBottom:22}}>
          <h3 style={{fontSize:14, fontWeight:700, color:K.amb, marginBottom:8}}>👑 Configuração Admin (Invisível para Produtores)</h3>
          {!f.isCampaign && (
            <>
              <Inp label="Taxa da Plataforma (%)" type="number" step="0.1" value={f.platformFee} onChange={e=>setF({...f,platformFee:Number(e.target.value)})}/>
              <p style={{fontSize:11, color:K.amb, marginTop:-8, marginBottom:12}}>Define a porcentagem retida para a plataforma neste evento. Padrão: 7.5%.</p>
            </>
          )}
          <Inp label="Access Token Direto MP (Opcional)" type="text" value={f.mpAccessToken||""} onChange={e=>setF({...f,mpAccessToken:e.target.value})}/>
          <p style={{fontSize:11, color:K.amb, marginTop:-8, marginBottom:12}}>Sobrescreve o OAuth. O dinheiro cai direto nessa conta (Sem taxa de plataforma).</p>
          <Inp label="Webhook URL Personalizado (Opcional)" type="text" value={f.webhookUrl||""} onChange={e=>setF({...f,webhookUrl:e.target.value})}/>
          <p style={{fontSize:11, color:K.amb, marginTop:-8}}>URL externa que receberá os POSTs quando o pagamento for aprovado.</p>
        </div>
      )}
      {errMsg&&<div style={{background:K.redBg,border:`1px solid ${K.red}30`,borderRadius:K.r.m,padding:"10px 14px",marginBottom:14,fontSize:13,color:K.red}}>⚠️ {errMsg}</div>}
      <Btn full sz="lg" loading={loading} onClick={publicar}>{f.id ? "💾 Salvar alterações" : "🚀 Publicar evento"}</Btn>
    </div>
  );

  return(
    <div style={{display:"flex",minHeight:"100vh",fontFamily:"'Open Sans',sans-serif"}}>
      <style>{G}{`
        @media(max-width:768px){
          .sidebar-panel{transform:translateX(-280px)!important}
          .sidebar-panel.open{transform:translateX(0)!important}
          .main-content{margin-left:0!important}
        }
      `}</style>

      {/* Overlay mobile */}
      {sidebarOpen && window.innerWidth <= 768 && (
        <div onClick={()=>setSidebarOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:99}} />
      )}

      {/* ═══ SIDEBAR ═══ */}
      <aside className={`sidebar-panel${sidebarOpen?" open":""}`} style={{
        width:280, background:"#0F172A", color:"#fff",
        display:"flex", flexDirection:"column",
        position:"fixed", top:0, left:0, bottom:0, zIndex:100,
        transition:"transform 0.3s cubic-bezier(0.4,0,0.2,1)",
        transform: sidebarOpen ? "translateX(0)" : "translateX(-280px)",
        boxShadow: "4px 0 24px rgba(0,0,0,0.2)"
      }}>
        {/* Logo */}
        <div style={{padding:"24px 22px 20px", borderBottom:"1px solid rgba(255,255,255,0.07)"}}>
          <p style={{fontFamily:"'Poppins',sans-serif", fontSize:21, fontWeight:800, margin:0, letterSpacing:"-0.3px"}}>
            Eventos<span style={{color:"#009EE3"}}>&</span>Encontros
          </p>
          <p style={{fontSize:11, color:"rgba(255,255,255,0.35)", marginTop:5, fontWeight:600, letterSpacing:"0.5px", textTransform:"uppercase"}}>Painel do Produtor</p>
        </div>
        
        {/* Navigation */}
        <nav style={{flex:1, paddingTop:12, overflowY:"auto"}}>
          <p style={{fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.25)", padding:"12px 22px 8px", textTransform:"uppercase", letterSpacing:"1.2px"}}>Menu Principal</p>
          
          <SideItem icon="📊" label="Dashboard" active={tab==="overview" && !selectedEvId}
            onClick={()=>{setSelectedEvId(null);setIsCreating(false);navTo("overview");}} />
          <SideItem icon="📅" label="Meus Eventos" active={tab==="eventos" && !selectedEvId}
            badge={meusEventos.length||null}
            onClick={()=>{setSelectedEvId(null);setIsCreating(false);navTo("eventos");}} />
          <SideItem icon="➕" label="Criar Evento" active={tab==="criar"}
            onClick={()=>{setSelectedEvId(null);setIsCreating(true);navTo("criar");setF({id:null,title:"",description:"",category:"Outro",emoji:"🎪",color:"#111111",date:"",time:"09:00",endTime:"18:00",location:"",city:"",state:"",price:0,spots:200,image:"",gallery:"",platformFee:7.5,staffEmails:"",isCampaign:false,mpAccessToken:"",webhookUrl:""});}} />
          
          {/* Event-specific sub-menu */}
          {selectedEvId && ev && (
            <>
              <div style={{margin:"20px 22px 8px", paddingTop:16, borderTop:"1px solid rgba(255,255,255,0.07)"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                  <span style={{fontSize:20}}>{ev.emoji}</span>
                  <div style={{minWidth:0}}>
                    <p style={{fontSize:12, fontWeight:700, color:"rgba(255,255,255,0.7)", margin:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{ev.title}</p>
                    <p style={{fontSize:10, color:"rgba(255,255,255,0.3)", margin:0}}>{ev.isCampaign?"Campanha":fmtD(ev.date)?.split(",")[0]}</p>
                  </div>
                </div>
              </div>
              
              <SideItem icon="📈" label="Dashboard" active={tab==="dash"} indent
                onClick={()=>navTo("dash")} />
              <SideItem icon="👥" label="Participantes" active={tab==="parts"} indent
                badge={ev.sold||null}
                onClick={()=>navTo("parts")} />
              <SideItem icon="💳" label="Mercado Pago" active={tab==="mp"} indent
                onClick={()=>navTo("mp")} />
              {!ev.isCampaign && (
                <SideItem icon="📸" label="Portaria" active={tab==="portaria"} indent
                  onClick={()=>navTo("portaria")} />
              )}
              <SideItem icon="✏️" label="Editar Evento" active={tab==="edit"} indent
                onClick={()=>{handleEdit(ev);}} />
            </>
          )}
        </nav>
        
        {/* Bottom */}
        <div style={{padding:"16px 22px 20px", borderTop:"1px solid rgba(255,255,255,0.07)", background:"rgba(0,0,0,0.15)"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
            <div style={{width:32,height:32,borderRadius:999,background:"rgba(255,255,255,0.08)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>👤</div>
            <p style={{fontSize:12, color:"rgba(255,255,255,0.45)", margin:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{user?.email}</p>
          </div>
          <button onClick={onClose} style={{background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.08)",color:"rgba(255,255,255,0.6)",fontSize:13,cursor:"pointer",padding:"9px 16px",borderRadius:K.r.m,width:"100%",fontWeight:600,fontFamily:"inherit",transition:"all 0.15s"}}
            onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.1)"}
            onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.06)"}>
            ← Voltar ao site
          </button>
        </div>
      </aside>
      
      {/* ═══ MAIN CONTENT ═══ */}
      <main className="main-content" style={{flex:1, marginLeft: sidebarOpen ? 280 : 0, transition:"margin 0.3s cubic-bezier(0.4,0,0.2,1)", background:K.bg, minHeight:"100vh"}}>
        {/* Top bar */}
        <header style={{background:K.sur, borderBottom:`1px solid ${K.brd}`, padding:"0 28px", height:60, display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:10, boxShadow:K.sh.s}}>
          <div style={{display:"flex",alignItems:"center",gap:14}}>
            <button onClick={()=>setSidebarOpen(!sidebarOpen)} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:K.mid,display:"flex",alignItems:"center",padding:4}} title="Menu">☰</button>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                {selectedEvId && <button onClick={()=>{setSelectedEvId(null);navTo("eventos")}} style={{background:"none",border:"none",color:K.lt,cursor:"pointer",fontSize:12,padding:0,fontFamily:"inherit"}}>Eventos ›</button>}
                <p style={{fontSize:15,fontWeight:700,color:K.ink,margin:0}}>{breadcrumb}</p>
              </div>
              {selectedEvId && ev && <p style={{fontSize:12,color:K.mid,margin:0}}>{ev.emoji} {ev.title}</p>}
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <Chip>Organizador</Chip>
          </div>
        </header>
        
        {/* Content */}
        <div style={{padding:"28px 28px",maxWidth:1100}}>
          
          {/* ── OVERVIEW (Dashboard Geral) ── */}
          {tab==="overview" && !selectedEvId && (
            <div>
              <div style={{marginBottom:28}}>
                <h2 style={{fontFamily:"'Poppins',sans-serif",fontSize:26,color:K.ink,marginBottom:4}}>Bem-vindo de volta! 👋</h2>
                <p style={{color:K.mid,fontSize:14}}>Aqui está um resumo geral dos seus eventos.</p>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:16,marginBottom:32}}>
                {[[meusEventos.length,"Eventos Ativos","🎪","#6366F1"],[totalInsc.toLocaleString("pt-BR"),"Inscrições Totais","👥","#009EE3"],[`R$ ${totalRec.toLocaleString("pt-BR",{minimumFractionDigits:2})}`,"Receita Total","💰","#10B981"],[`${meusEventos.length?Math.round(meusEventos.reduce((s,e)=>s+((e.sold||0)/e.spots)*100,0)/meusEventos.length):0}%`,"Ocupação Média","📊","#F59E0B"]].map(([v,l,i,c])=>(
                  <div key={l} className="fu" style={{background:K.sur,border:`1px solid ${K.brd}`,borderRadius:K.r.xl,padding:"22px 20px",boxShadow:K.sh.s,position:"relative",overflow:"hidden"}}>
                    <div style={{position:"absolute",top:-8,right:-8,width:48,height:48,borderRadius:999,background:`${c}12`}} />
                    <span style={{fontSize:28}}>{i}</span>
                    <p style={{fontFamily:"'Poppins',sans-serif",fontSize:28,fontWeight:700,color:K.ink,marginTop:8,marginBottom:2}}>{v}</p>
                    <p style={{fontSize:12,color:K.lt}}>{l}</p>
                  </div>
                ))}
              </div>

              {/* Recent events quick list */}
              {meusEventos.length > 0 && (
                <div style={{background:K.sur,borderRadius:K.r.xl,border:`1px solid ${K.brd}`,overflow:"hidden",boxShadow:K.sh.s}}>
                  <div style={{padding:"18px 22px",borderBottom:`1px solid ${K.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <h3 style={{fontFamily:"'Poppins',sans-serif",fontSize:16,color:K.ink,margin:0}}>Eventos Recentes</h3>
                    <Btn sz="sm" v="ghost" onClick={()=>navTo("eventos")}>Ver todos →</Btn>
                  </div>
                  {meusEventos.slice(0,5).map(item => (
                    <div key={item.id} onClick={()=>{setSelectedEvId(item.id);navTo("dash",item.id);}} style={{padding:"14px 22px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:`1px solid ${K.brd}`,cursor:"pointer",transition:"background 0.1s"}}
                      onMouseEnter={e=>e.currentTarget.style.background=K.bg} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <div style={{display:"flex",alignItems:"center",gap:12}}>
                        <div style={{width:40,height:40,borderRadius:K.r.m,background:item.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>{item.emoji}</div>
                        <div>
                          <p style={{fontSize:14,fontWeight:600,color:K.ink,margin:0}}>{item.title}</p>
                          <p style={{fontSize:12,color:K.lt,margin:0}}>{item.isCampaign?"Campanha":fmtD(item.date)?.split(",")[0]} • {item.city}</p>
                        </div>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <p style={{fontSize:14,fontWeight:700,color:K.ink,margin:0}}>👥 {item.sold||0}</p>
                        <p style={{fontSize:12,color:K.lt,margin:0}}>R$ {((item.sold||0)*item.price).toLocaleString("pt-BR",{minimumFractionDigits:2})}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── EVENTOS (Grid de eventos) ── */}
          {tab==="eventos" && !selectedEvId && (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
                <h2 style={{fontFamily:"'Poppins',sans-serif",fontSize:24,color:K.ink}}>Meus Eventos</h2>
                <Btn onClick={()=>{setIsCreating(true);navTo("criar");setF({id:null,title:"",description:"",category:"Outro",color:"#111111",date:"",time:"09:00",endTime:"18:00",location:"",city:"",state:"",price:0,spots:200,image:"",gallery:""});}}>➕ Criar Novo Evento</Btn>
              </div>
              
              {meusEventos.length === 0 ? (
                <div style={{textAlign:"center",padding:60,color:K.mid,background:K.sur,borderRadius:K.r.xl,border:`1px dashed ${K.brd}`}}>
                  <p style={{fontSize:48,marginBottom:16}}>📅</p>
                  <h3 style={{fontFamily:"'Poppins',sans-serif",fontSize:20,color:K.ink,marginBottom:8}}>Nenhum evento criado</h3>
                  <p style={{marginBottom:24}}>Comece agora a criar seu primeiro evento!</p>
                  <Btn onClick={()=>{setIsCreating(true);navTo("criar");}}>Criar Primeiro Evento</Btn>
                </div>
              ) : (
                <div className="mob-grid" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))",gap:18}}>
                  {meusEventos.map(item => (
                    <div key={item.id} onClick={()=>{setSelectedEvId(item.id);navTo("dash",item.id);}} className="hov" style={{background:K.sur,border:`1px solid ${K.brd}`,borderRadius:K.r.xl,overflow:"hidden",cursor:"pointer",boxShadow:K.sh.s}}>
                      <div style={{height:8,background:item.color}} />
                      <div style={{padding:20}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                          <div style={{width:48,height:48,borderRadius:K.r.m,background:item.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24}}>{item.emoji}</div>
                          <div style={{display:"flex",gap:6}}>
                            {item.featured && <span style={{fontSize:12,fontWeight:700,background:"#FEF08A",color:"#854D0E",padding:"2px 8px",borderRadius:999}}>⭐</span>}
                            <Chip c={item.registrationClosed?K.red:K.grn} bg={item.registrationClosed?K.redBg:K.grnBg}>{item.registrationClosed?"Pausado":"Ativo"}</Chip>
                          </div>
                        </div>
                        <h3 style={{fontFamily:"'Poppins',sans-serif",fontSize:18,color:K.ink,marginBottom:4}}>{item.title}</h3>
                        <p style={{fontSize:13,color:K.mid,marginBottom:14}}>
                          {item.isCampaign ? "Campanha Permanente" : fmtD(item.date)?.split(",")[0]} • {item.city}
                        </p>
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:K.mid,borderTop:`1px solid ${K.brd}`,paddingTop:14}}>
                          <span>👥 {item.sold||0} inscritos</span>
                          <span style={{fontWeight:700,color:K.ink}}>R$ {((item.sold||0)*item.price).toLocaleString("pt-BR",{minimumFractionDigits:2})}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── CRIAR EVENTO ── */}
          {tab==="criar" && renderForm()}

          {/* ── EVENTO SELECIONADO ── */}
          {selectedEvId && ev && (
            <div>
              {/* Event header */}
              <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:28,flexWrap:"wrap"}}>
                <div style={{width:56,height:56,borderRadius:K.r.l,background:ev.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,flexShrink:0}}>{ev.emoji}</div>
                <div style={{flex:1,minWidth:0}}>
                  <h2 style={{fontFamily:"'Poppins',sans-serif",fontSize:24,color:K.ink,lineHeight:1.1,margin:0}}>{ev.title}</h2>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginTop:4,fontSize:13,color:K.mid,flexWrap:"wrap"}}>
                    {!ev.isCampaign && <span>{fmtD(ev.date)}</span>}
                    {!ev.isCampaign && <span>•</span>}
                    <span>{ev.city}</span>•
                    <span style={{color:ev.registrationClosed?K.red:K.grn,fontWeight:700}}>{ev.registrationClosed?"Vendas Pausadas":ev.isCampaign?"Doações Abertas":"Vendas Abertas"}</span>
                  </div>
                </div>
              </div>

              {/* ── Tab: Dashboard do Evento ── */}
              {tab==="dash" && (
                <div>
                  <div style={{display:"flex",gap:12,marginBottom:24,flexWrap:"wrap"}}>
                    <Btn sz="sm" v="out" onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/evento/${ev.id}`);
                      alert("Link do evento copiado para a área de transferência!");
                    }}>🔗 Copiar Link de Divulgação</Btn>
                    {ev.price > 0 && (
                      <Btn sz="sm" v="out" onClick={async () => {
                        try {
                          const res = await criarPagamento(ev.id, 1);
                          if (res.linkPagamento) {
                            navigator.clipboard.writeText(res.linkPagamento);
                            alert("Link de pagamento direto copiado para o WhatsApp!");
                          }
                        } catch(e) { alert("Erro ao gerar link de pagamento rápido: " + e.message); }
                      }}>🛒 Link de Pagamento Rápido</Btn>
                    )}
                  </div>

                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:14,marginBottom:24}}>
                    {[[ev.sold||0,"Inscritos","👥"],[ev.spotsLeft!==undefined?ev.spotsLeft:ev.spots,"Vagas Livres","🎫"],[`R$ ${((ev.sold||0)*ev.price).toLocaleString("pt-BR",{minimumFractionDigits:2})}`,"Receita Bruta","💰"],[`${ev.spots?Math.round(((ev.sold||0)/ev.spots)*100):0}%`,"Lotação","📊"]].map(([v,l,i])=>(
                      <div key={l} style={{background:K.sur,border:`1px solid ${K.brd}`,borderRadius:K.r.xl,padding:"18px 18px 14px",boxShadow:K.sh.s}}>
                        <span style={{fontSize:24}}>{i}</span>
                        <p style={{fontFamily:"'Poppins',sans-serif",fontSize:26,fontWeight:700,color:K.ink,marginTop:6}}>{v}</p>
                        <p style={{fontSize:12,color:K.lt,marginTop:2}}>{l}</p>
                      </div>
                    ))}
                  </div>

                  {/* Transparência Financeira */}
                  {!ev.isCampaign && (
                    <div style={{background:K.sur,borderRadius:K.r.xl,padding:24,border:`1px solid ${K.brd}`,marginBottom:24,boxShadow:K.sh.s}}>
                      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
                        <h3 style={{fontFamily:"'Poppins',sans-serif",fontSize:18,color:K.ink}}>Transparência Financeira (Split Automático)</h3>
                        <Chip bg={K.alt} c={K.ink}>Taxa atual: {ev.isCampaign ? 0 : (ev.platformFee !== undefined ? ev.platformFee : 7.5)}%</Chip>
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:20}}>
                        <div style={{background:K.bg,borderRadius:K.r.l,padding:20,border:`1px dashed ${K.brd}`}}>
                          <p style={{fontSize:12,fontWeight:700,color:K.lt,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:4}}>Faturamento Bruto</p>
                          <p style={{fontFamily:"'Poppins',sans-serif",fontSize:24,color:K.ink,fontWeight:800}}>R$ {((ev.sold||0)*ev.price).toLocaleString("pt-BR",{minimumFractionDigits:2})}</p>
                          <p style={{fontSize:12,color:K.mid,marginTop:4}}>Total pago pelos clientes.</p>
                        </div>
                        <div style={{background:`${K.mp}15`,borderRadius:K.r.l,padding:20,border:`1px solid ${K.mp}30`}}>
                          <p style={{fontSize:12,fontWeight:700,color:K.mp,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:4}}>Taxa da Plataforma</p>
                          <p style={{fontFamily:"'Poppins',sans-serif",fontSize:24,color:K.mp,fontWeight:800}}>R$ {(((ev.sold||0)*ev.price) * ((ev.isCampaign ? 0 : (ev.platformFee !== undefined ? ev.platformFee : 7.5)) / 100)).toLocaleString("pt-BR",{minimumFractionDigits:2})}</p>
                          <p style={{fontSize:12,color:K.ink,opacity:0.7,marginTop:4}}>Transferido p/ Eventos e Encontros.</p>
                        </div>
                        <div style={{background:`${K.grn}15`,borderRadius:K.r.l,padding:20,border:`1px solid ${K.grn}30`}}>
                          <p style={{fontSize:12,fontWeight:700,color:K.grn,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:4}}>Seu Líquido (Mercado Pago)</p>
                          <p style={{fontFamily:"'Poppins',sans-serif",fontSize:24,color:K.grn,fontWeight:800}}>R$ {(((ev.sold||0)*ev.price) * (1 - (ev.isCampaign ? 0 : (ev.platformFee !== undefined ? ev.platformFee : 7.5)) / 100)).toLocaleString("pt-BR",{minimumFractionDigits:2})}</p>
                          <p style={{fontSize:12,color:K.ink,opacity:0.7,marginTop:4}}>Livre na conta da Diocese/PJ.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(250px,1fr))",gap:14}}>
                    <div style={{background:K.sur,borderRadius:K.r.xl,padding:22,border:`1px solid ${K.brd}`}}>
                      <h3 style={{fontFamily:"'Poppins',sans-serif",fontSize:16,color:K.ink,marginBottom:12}}>Status na Vitrine</h3>
                      <p style={{fontSize:13,color:K.mid,marginBottom:16}}>Destaque seu evento no topo da página inicial para ganhar mais visibilidade e acelerar as vendas.</p>
                      <Btn sz="sm" v={ev.featured ? "out" : "ghost"} sx={{background: ev.featured ? "transparent" : "#FEF08A", color: ev.featured ? K.ink : "#854D0E", borderColor: ev.featured ? K.brd : "transparent"}} onClick={async () => {
                        try { await atualizarEvento(ev.id, {featured: !ev.featured}); alert(ev.featured ? "Destaque removido" : "Evento destacado no topo!"); }
                        catch(err) { alert("Erro: " + err.message); }
                      }}>{ev.featured ? "⭐ Remover Destaque" : "⭐ Ativar Destaque"}</Btn>
                    </div>
                    <div style={{background:K.sur,borderRadius:K.r.xl,padding:22,border:`1px solid ${K.brd}`}}>
                      <h3 style={{fontFamily:"'Poppins',sans-serif",fontSize:16,color:K.ink,marginBottom:12}}>Trava de Vendas</h3>
                      <p style={{fontSize:13,color:K.mid,marginBottom:16}}>Se você precisar congelar as vendas temporariamente (ex: lote virando), pause aqui.</p>
                      <Btn sz="sm" v={ev.registrationClosed ? "mp" : "out"} sx={{background: ev.registrationClosed ? K.grn : "transparent", color: ev.registrationClosed ? "#fff" : K.red, borderColor: ev.registrationClosed ? "transparent" : K.brd}} onClick={async () => {
                        try { await atualizarEvento(ev.id, {registrationClosed: !ev.registrationClosed}); alert(ev.registrationClosed ? "Vendas reabertas!" : "Vendas pausadas!"); }
                        catch(err) { alert("Erro: " + err.message); }
                      }}>{ev.registrationClosed ? "🔓 Reabrir Vendas" : "🔒 Pausar Vendas"}</Btn>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Tab: Participantes ── */}
              {tab==="parts" && (
                <div style={{background:K.sur,borderRadius:K.r.xl,padding:28,boxShadow:K.sh.s,border:`1px solid ${K.brd}`}}>
                  <ParticipantesTab eventos={[ev]} />
                </div>
              )}

              {/* ── Tab: Mercado Pago ── */}
              {tab==="mp" && (
                <div style={{background:K.sur,borderRadius:K.r.xl,padding:28,maxWidth:600,boxShadow:K.sh.s,border:`1px solid ${K.brd}`}}>
                  <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:16}}>
                    <div style={{width:48,height:48,borderRadius:K.r.m,background:"#009EE3",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,fontWeight:800}}>MP</div>
                    <h3 style={{fontFamily:"'Poppins',sans-serif",fontSize:20,color:K.ink}}>Recebedor Financeiro</h3>
                  </div>
                  <p style={{fontSize:14,color:K.mid,marginBottom:24,lineHeight:1.6}}>Conecte a conta do Mercado Pago que deve receber todo o dinheiro das vendas <b>exclusivamente para este evento ({ev.title})</b>. A taxa da plataforma será descontada automaticamente de cada transação de forma limpa.</p>
                  <div style={{background:K.alt,padding:20,borderRadius:K.r.m,border:`1px solid ${K.brd}`}}>
                    <EventMPStatus ev={ev} user={user} />
                  </div>
                </div>
              )}

              {/* ── Tab: Portaria ── */}
              {tab==="portaria" && (
                <div style={{background:K.sur,borderRadius:K.r.xl,padding:28,maxWidth:600,boxShadow:K.sh.s,border:`1px solid ${K.brd}`}}>
                  <h3 style={{fontFamily:"'Poppins',sans-serif",fontSize:20,color:K.ink,marginBottom:12}}>📸 Aplicativo de Portaria (Leitor de QR)</h3>
                  <p style={{fontSize:14,color:K.mid,marginBottom:24,lineHeight:1.6}}>
                    Esse link abre a Câmera de Portaria bloqueada exclusivamente para <b>{ev.title}</b>. O voluntário que abrir esse link só precisará apontar o celular para os ingressos.<br/><br/>
                    <b>Segurança:</b> Se alguém apresentar o ingresso de outro evento, a portaria piscará em vermelho negando a entrada! Não é necessário que o voluntário tenha login na plataforma.
                  </p>
                  <div style={{display:"flex",flexDirection:"column",gap:12}}>
                    <Btn sz="lg" onClick={() => {
                      window.open(`${window.location.origin}/portaria/${ev.id}`, "_blank");
                    }}>Acessar Portaria agora</Btn>
                    <Btn sz="lg" v="out" onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/portaria/${ev.id}`);
                      alert("Link de Portaria copiado! Envie no WhatsApp dos seus voluntários.");
                    }}>Copiar Link para Voluntários</Btn>
                  </div>
                </div>
              )}

              {/* ── Tab: Editar ── */}
              {tab==="edit" && (
                <>
                  {renderForm()}
                  <div style={{maxWidth:640,marginTop:24,textAlign:"center"}}>
                    <Btn sz="sm" v="ghost" sx={{color:K.red}} onClick={() => handleDelete(ev)}>🗑️ Excluir permanentemente este evento</Btn>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
/* ─── Evento Detail Route ─────────────────────────────────────────────────────── */
function EventoDetailRoute({ eventos, setCheckout, setAuthModal, setMeusIngressos }) {
  const { id } = useParams();
  const { user, sair } = useAuth();
  const sel = eventos.find(e => e.id === id);
  const navigate = useNavigate();

  const [hasPending, setHasPending] = useState(false);
  useEffect(() => {
    if (!user || !sel) return;
    const unsub = escutarIngressos(user.uid, (list) => {
      const pending = list.find(i => i.eventoId === sel.id && i.status === "pending");
      setHasPending(!!pending);
    });
    return unsub;
  }, [user, sel]);

  if (!sel) return <div style={{padding:60,textAlign:"center",fontFamily:"'Poppins',sans-serif",fontSize:24}}>Evento não encontrado.<br/><Btn sx={{marginTop:20}} onClick={()=>navigate("/")}>Voltar</Btn></div>;

  const isPast = sel.date ? new Date(sel.date + "T23:59:59") < new Date() : false;
  const isClosed = sel.registrationClosed || isPast;
  const hasImg = isValidImage(sel.image);

  return (
    <>
      <style>{G}</style>
      <nav style={{background:K.sur,borderBottom:`1px solid ${K.brd}`,padding:"0 24px",height:56,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:50,boxShadow:K.sh.s}}>
        <button onClick={()=>navigate("/")} style={{fontFamily:"'Poppins',sans-serif",fontSize:22,fontWeight:800,color:K.ink,background:"none",border:"none",cursor:"pointer"}}>Eventos<span style={{color:"#009EE3"}}>&</span>Encontros</button>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {user?<><Btn sz="sm" v="ghost" onClick={()=>setMeusIngressos(true)}>Meus ingressos</Btn><button onClick={sair} style={{fontSize:12,color:K.lt,background:"none",border:"none",cursor:"pointer"}}>Sair</button></>:<Btn sz="sm" v="out" onClick={()=>setAuthModal(true)}>Entrar</Btn>}
          {isClosed ? (
            <Btn sz="sm" sx={{background:K.mid, cursor:"not-allowed"}} disabled>Encerrado</Btn>
          ) : (
            <Btn sz="sm" sx={{background:sel.color}} onClick={()=>{
              setCheckout(true);
            }}>{sel.isCampaign ? "Fazer Contribuição" : "Garantir ingresso"}</Btn>
          )}
        </div>
      </nav>
      {/* Detalhe inline */}
      <div style={{background:hasImg?`url(${sel.image}) center/cover no-repeat`:`linear-gradient(160deg,${sel.color} 0%,${sel.color}99 50%,${sel.color}22 100%)`,padding:"52px 24px 32px",position:"relative"}}>
        {hasImg&&<div style={{position:"absolute",inset:0,background:"linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0.2))"}}/>}
        <div style={{maxWidth:840,margin:"0 auto",position:"relative",zIndex:2}}>
          <button onClick={()=>navigate("/")} style={{background:"rgba(255,255,255,.2)",border:"none",color:"#fff",borderRadius:K.r.m,padding:"8px 16px",cursor:"pointer",fontWeight:600,fontSize:13,marginBottom:22,display:"flex",alignItems:"center",gap:6}}>← Voltar</button>
          <div style={{display:"flex",gap:16,flexWrap:"wrap",alignItems:"flex-end"}}>
            {!hasImg&&<span style={{fontSize:60}}>{sel.emoji}</span>}
            <div style={{color:"#fff",flex:1,minWidth:0}}>
              <Chip c="#fff" bg="rgba(0,0,0,.22)" sx={{marginBottom:10}}>{sel.category||sel.cat}</Chip>
              <h1 className="fu" style={{fontFamily:"'Poppins',sans-serif",fontSize:"clamp(24px,5vw,44px)",lineHeight:1.1,marginBottom:7,wordBreak:"break-word"}}>{sel.title}</h1>
              <p className="fu2" style={{opacity:.8,fontSize:14}}>por {sel.organizer||sel.org}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="mob-col" style={{maxWidth:840,margin:"0 auto",padding:"0 24px 80px",display:"grid",gridTemplateColumns:"1fr 290px",gap:22,alignItems:"start"}}>
        <div style={{paddingTop:22}}>
          {[
            ["Sobre o evento",<p style={{color:K.mid,lineHeight:1.75,fontSize:15}}>{sel.description||sel.desc}</p>],
            ["Informações",<div>{
              [
                !sel.isCampaign && ["📅","Data",fmtD(sel.date)],
                !sel.isCampaign && ["🕐","Horário",`${sel.time}–${sel.endTime||sel.fim}`],
                ["📍","Local",`${sel.location||sel.local}, ${sel.city}/${sel.state}`],
                !sel.isCampaign && ["🎫","Vagas",`${sel.spotsLeft!==undefined?sel.spotsLeft:sel.spots} de ${sel.spots}`],
                ["👤","Organização",sel.organizer||sel.org]
              ].filter(Boolean).map(([i,l,v])=>(
            <div key={l} style={{display:"flex",gap:12,padding:"11px 0",borderBottom:`1px solid ${K.brd}`}}>
              <span style={{fontSize:17,flexShrink:0}}>{i}</span>
              <div><p style={{fontSize:11,color:K.lt,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:1}}>{l}</p><p style={{fontSize:14,color:K.ink,fontWeight:500}}>{v}</p></div>
            </div>
          ))}</div>]
          ].map(([t,c])=>(
            <div key={t} style={{background:K.sur,border:`1px solid ${K.brd}`,borderRadius:K.r.xl,padding:22,marginBottom:14,boxShadow:K.sh.s}}>
              <h2 style={{fontFamily:"'Poppins',sans-serif",fontSize:19,color:K.ink,marginBottom:14}}>{t}</h2>
              {c}
            </div>
          ))}

          {sel.gallery && sel.gallery.split("\n").map(u=>u.trim()).filter(u=>u.startsWith("http")).length > 0 && (
            <div style={{background:K.sur,border:`1px solid ${K.brd}`,borderRadius:K.r.xl,padding:22,marginBottom:14,boxShadow:K.sh.s}}>
              <h2 style={{fontFamily:"'Poppins',sans-serif",fontSize:19,color:K.ink,marginBottom:14}}>📸 Galeria Pós-Evento</h2>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:12}}>
                {sel.gallery.split("\n").map(u=>u.trim()).filter(u=>u.startsWith("http")).map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noreferrer" style={{display:"block",aspectRatio:"1",borderRadius:K.r.m,overflow:"hidden",boxShadow:K.sh.s,border:`1px solid ${K.brd}`}}>
                    <img src={url} alt={`Mural ${i+1}`} style={{width:"100%",height:"100%",objectFit:"cover",transition:"transform 0.2s"}} onMouseEnter={e=>e.currentTarget.style.transform="scale(1.05)"} onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"} loading="lazy"/>
                  </a>
                ))}
              </div>
            </div>
          )}

        </div>
        <div style={{position:"sticky",top:70,paddingTop:22}}>
          <div style={{background:K.sur,border:`1px solid ${K.brd}`,borderRadius:K.r.xl,padding:22,boxShadow:K.sh.l}}>
            <p style={{fontFamily:"'Poppins',sans-serif",fontSize:28,fontWeight:700,color:sel.price===0?K.grn:K.ink,marginBottom:14}}>{sel.isCampaign ? "Campanha Permanente" : fmtP(sel.price)}</p>
            {sel.price>0&&!sel.isCampaign&&<p style={{fontSize:12,color:K.lt,marginBottom:14}}>Parcelável em até 12x no cartão</p>}
            {hasPending ? (
              <Btn full sz="lg" onClick={()=>setMeusIngressos(true)} sx={{background:K.amb,borderRadius:K.r.l,marginBottom:14,animation:"pulse 2s infinite"}}>
                Pagar Inscrição Pendente 🛒
              </Btn>
            ) : isClosed ? (
              <Btn full sz="lg" disabled sx={{background:K.mid,borderRadius:K.r.l,marginBottom:14,cursor:"not-allowed"}}>
                Inscrições Encerradas
              </Btn>
            ) : (
              <Btn full sz="lg" onClick={()=>{
                setCheckout(true);
              }} sx={{background:sel.color,borderRadius:K.r.l,marginBottom:14}}>
                {sel.isCampaign ? "Quero Contribuir" : sel.price===0?"Inscrever-se gratuitamente":"Garantir ingresso"} →
              </Btn>
            )}
            <Btn full v="out" onClick={() => {
              if (navigator.share) {
                navigator.share({ url: window.location.href }).catch(()=>{});
              } else {
                navigator.clipboard.writeText(window.location.href);
                alert("Link copiado para a área de transferência!");
              }
            }} sx={{marginBottom:14}}>🔗 Compartilhar Evento</Btn>
            {["✓ Ingresso digital com QR Code","✓ Pagamento via Mercado Pago","✓ Cartão • PIX • Boleto","✓ Confirmação automática por e-mail","✓ Atualização em tempo real"].map(i=>(
              <p key={i} style={{fontSize:12,color:K.mid,marginBottom:5}}>{i}</p>
            ))}
          </div>
        </div>
      </div>
      <Helmet>
        <title>{sel.title} - Eventos & Encontros</title>
        <meta property="og:title" content={sel.title} />
        <meta property="og:description" content={sel.description} />
      </Helmet>
    </>
  );
}

/* ─── App Principal ─────────────────────────────────────────────────────────── */
function AppInner(){
  const {user,userData,loading:authLoading,sair}=useAuth();
  const [eventos,setEventos]=useState([]);
  const [loadingEvts,setLoadingEvts]=useState(true);
  const [authModal,setAuthModal]=useState(false);
  const [checkout,setCheckout]=useState(false);
  const [sel,setSel]=useState(null);
  const [meusIngressos,setMeusIngressos]=useState(false);
  const [toast,setToast]=useState(null);
  const [busca,setBusca]=useState("");
  const [catF,setCatF]=useState("Todos");
  const [mesF,setMesF]=useState("Todos");
  const [modalInfo,setModalInfo]=useState(null);

  const navigate = useNavigate();

  const mesesMap = {
    "Todos": -1, "Janeiro": 0, "Fevereiro": 1, "Março": 2, "Abril": 3, "Maio": 4, "Junho": 5,
    "Julho": 6, "Agosto": 7, "Setembro": 8, "Outubro": 9, "Novembro": 10, "Dezembro": 11
  };
  const meses = Object.keys(mesesMap);

  // Escuta eventos em tempo real via Firestore
  useEffect(()=>{
    const unsub=escutarEventos(evs=>{
      const sorted = [...evs].sort((a,b) => {
        const dateA = a.date ? new Date(a.date + "T23:59:59") : new Date(0);
        const dateB = b.date ? new Date(b.date + "T23:59:59") : new Date(0);
        const pastA = dateA < new Date();
        const pastB = dateB < new Date();
        if (pastA !== pastB) return pastA ? 1 : -1;
        return pastA ? dateB - dateA : dateA - dateB; 
      });
      setEventos(sorted);
      setLoadingEvts(false);
    });
    return unsub;
  },[]);

  const addToast=useCallback((msg,type="success")=>{setToast({msg,type});setTimeout(()=>setToast(null),4000);},[]);

  const cats=["Todos",...Array.from(new Set(eventos.map(e=>e.category||e.cat||"Outro")))];
  const filtrados=eventos.filter(e=>{
    const searchMatch = (e.title||"").toLowerCase().includes(busca.toLowerCase()) || (e.city||"").toLowerCase().includes(busca.toLowerCase());
    const catMatch = catF === "Todos" || (e.category||e.cat) === catF;
    const monthMatch = mesF === "Todos" || (e.date && new Date(e.date + "T12:00:00").getMonth() === mesesMap[mesF]);
    return searchMatch && catMatch && monthMatch;
  });
  const destaques=eventos.filter(e=>e.featured);

  if(authLoading)return<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh"}}><Spinner/></div>;

  // Renderiza o painel com rotas completas agora (ClientDashboard, Painel full page)

  

  return(
    <div style={{fontFamily:"'Open Sans',sans-serif",background:K.bg,color:K.ink,minHeight:"100vh"}}>
      <style>{G}</style>
      <Routes>
        <Route path="/portaria/:id" element={<Portaria />} />
        <Route path="/admin" element={<Painel eventos={eventos} onClose={() => navigate("/")} onCriado={() => addToast("Evento publicado!")} />} />
        <Route path="/meus-ingressos" element={<ClientDashboard />} />
        <Route path="/historia-rcc" element={<HistoryRCC />} />
        <Route path="/politica-devolucao" element={<RefundPolicy />} />
        <Route path="/politica-de-seguranca" element={<SecurityPolicy />} />
        <Route path="/termos" element={<TermsOfUse />} />
        <Route path="/privacidade" element={<PrivacyPolicy />} />
        <Route path="/evento/:id" element={<EventoDetailRoute eventos={eventos} setCheckout={(v)=>{
          if(v) {
            const id = window.location.pathname.split("/").pop();
            setSel(eventos.find(e => e.id === id));
          }
          setCheckout(v);
        }} setAuthModal={setAuthModal} setMeusIngressos={setMeusIngressos} />} />
        <Route path="/" element={
          <>
            {/* NAV */}
      <nav style={{background:K.sur,borderBottom:`1px solid ${K.brd}`,padding:"0 24px",height:58,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:50,boxShadow:K.sh.s}}>
        <div style={{display:"flex",alignItems:"center",gap:16}}>
          <span style={{fontFamily:"'Poppins',sans-serif",fontSize:23,fontWeight:800,color:K.ink,cursor:"pointer"}} onClick={()=>navigate("/")}>Eventos<span style={{color:"#009EE3"}}>&</span>Encontros</span>
          <button onClick={()=>navigate("/historia-rcc")} style={{background:"transparent",border:`1px solid ${K.brd}`,color:K.mid,padding:"6px 14px",borderRadius:K.r.p,cursor:"pointer",fontWeight:600,fontSize:13}}>A História da RCC</button>
        </div>
        <div style={{flex:1,maxWidth:500,margin:"0 20px",display:"flex",gap:8}}>
          <div style={{flex:1,position:"relative"}}>
            <span style={{position:"absolute",left:11,top:"50%",transform:"translateY(-50%)",color:K.lt,fontSize:14}}>🔍</span>
            <input value={busca} onChange={e=>setBusca(e.target.value)} placeholder="Buscar eventos ou cidades..." style={{width:"100%",background:K.alt,border:`1.5px solid ${K.brd}`,borderRadius:K.r.p,padding:"9px 12px 9px 34px",fontSize:14,color:K.ink,outline:"none"}}/>
          </div>
          <select value={mesF} onChange={e=>setMesF(e.target.value)} style={{background:K.alt,border:`1.5px solid ${K.brd}`,borderRadius:K.r.p,padding:"0 12px",fontSize:14,color:K.ink,outline:"none",fontWeight:600,cursor:"pointer"}}>
            {meses.map(m => <option key={m} value={m}>{m === "Todos" ? "Todos os Meses" : m}</option>)}
          </select>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {( (user?.email && btoa(user.email.toLowerCase()) === "ZGV2Lmxlb2NhcnZhbGhvQGdtYWlsLmNvbQ==") || userData?.role === "organizer") && <Btn sz="sm" v="ghost" onClick={()=>navigate("/admin")}>Painel do Produtor</Btn>}
          {user?(
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <Btn sz="sm" v="out" onClick={()=>navigate("/meus-ingressos")} className="hide-mob">Meus Ingressos</Btn>
              <div style={{display:"flex",alignItems:"center",gap:8,background:K.alt,padding:"4px 12px 4px 4px",borderRadius:999,cursor:"pointer"}} onClick={sair} title="Sair">
                <div style={{width:28,height:28,borderRadius:999,background:K.ink,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:12}}>
                  {(userData?.nome||user.displayName||user.email||"?")[0].toUpperCase()}
                </div>
                <span style={{fontSize:13,fontWeight:600,color:K.ink}} className="hide-mob">
                  {(userData?.nome||user.displayName||user.email)?.split(" ")[0]}
                </span>
              </div>
            </div>
          ):(
            <><Btn sz="sm" v="out" onClick={()=>{setAuthModal(true);}}>Entrar</Btn><Btn sz="sm" onClick={()=>{setAuthModal(true);}}>Criar conta</Btn></>
          )}
        </div>
      </nav>

      {/* HERO */}
      <div style={{background:`linear-gradient(145deg,${K.ink} 0%,#1f1f38 100%)`,color:"#fff",padding:"36px 24px",textAlign:"center",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-50,left:"8%",width:280,height:280,borderRadius:999,background:"rgba(255,61,90,.06)",filter:"blur(60px)"}}/>
        <div style={{position:"absolute",bottom:-40,right:"5%",width:220,height:220,borderRadius:999,background:"rgba(0,158,227,.06)",filter:"blur(50px)"}}/>
        <h1 className="fu2" style={{fontFamily:"'Poppins',sans-serif",fontSize:"clamp(26px,4vw,44px)",lineHeight:1.1,marginBottom:12}}>Evangelizar com renovado ardor missionário</h1>
        <p className="fu3" style={{fontSize:15,opacity:.7,maxWidth:480,margin:"0 auto 24px",lineHeight:1.5}}>Inscreva-se online, pague com segurança e receba seu ingresso confirmado na hora.</p>
        <div className="fu4" style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap",position:"relative",zIndex:2}}>
          <Btn sz="lg" sx={{background:K.red,borderRadius:K.r.p}} onClick={()=>document.getElementById("evs")?.scrollIntoView({behavior:"smooth"})}>Explorar eventos</Btn>
          {( (user?.email && btoa(user.email.toLowerCase()) === "ZGV2Lmxlb2NhcnZhbGhvQGdtYWlsLmNvbQ==") || userData?.role === "organizer") && <Btn sz="lg" sx={{background:"transparent",border:"1.5px solid rgba(255,255,255,.22)",color:"#fff",borderRadius:K.r.p}} onClick={()=>navigate("/admin")}>Acessar Painel</Btn>}
        </div>
      </div>

      {/* DESTAQUES */}
      {destaques.length>0&&(
        <div style={{maxWidth:1060,margin:"0 auto",padding:"52px 22px 0"}}>
          <h2 style={{fontFamily:"'Poppins',sans-serif",fontSize:24,color:K.ink,marginBottom:20}}>Em destaque</h2>
          <div className="mob-grid" style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16}}>
            {destaques.map(ev=><EventCard key={ev.id} ev={ev} onClick={()=>navigate(`/evento/${ev.id}`)}/>)}
          </div>
        </div>
      )}


      {/* TODOS OS EVENTOS */}
      <div id="evs" style={{maxWidth:1060,margin:"0 auto",padding:"48px 22px 80px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:12}}>
          <h2 style={{fontFamily:"'Poppins',sans-serif",fontSize:24,color:K.ink}}>Todos os eventos</h2>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {cats.map(c=><button key={c} onClick={()=>setCatF(c)} style={{padding:"6px 14px",borderRadius:K.r.p,border:`1.5px solid ${catF===c?K.ink:K.brd}`,background:catF===c?K.ink:K.sur,color:catF===c?"#fff":K.mid,fontSize:13,fontWeight:600,cursor:"pointer",transition:"all .15s"}}>{c}</button>)}
          </div>
        </div>
        {loadingEvts?<Spinner/>:filtrados.length===0?(
          <div style={{textAlign:"center",padding:"56px 0",color:K.lt}}>
            <p style={{fontSize:15,marginTop:10}}>Nenhum evento encontrado para estes filtros.</p>
            <p style={{fontSize:13,marginTop:6}}>Tente selecionar outro mês ou buscar por outra cidade.</p>
          </div>
        ):(
          <div className="mob-grid" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:16}}>
            {filtrados.map(ev=><EventCard key={ev.id} ev={ev} onClick={()=>navigate(`/evento/${ev.id}`)}/>)}
          </div>
        )}
      </div>

      {/* FOOTER */}
      <footer style={{background:"linear-gradient(180deg, #002230 0%, #00121a 100%)",color:"#fff",padding:"60px 24px 30px", borderTop:`1px solid ${K.ink}`}}>
        <div style={{maxWidth:1060,margin:"0 auto",display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(200px, 1fr))",gap:40}}>
          <div>
            <p style={{fontFamily:"'Poppins',sans-serif",fontSize:28,fontWeight:800,marginBottom:10}}>Eventos<span style={{color:"#009EE3"}}>&</span>Encontros</p>
            <p style={{opacity:.6,fontSize:14,lineHeight:1.6}}>A plataforma oficial para gestão e venda de ingressos para eventos, encontros e retiros.</p>
          </div>
          <div>
            <h4 style={{fontSize:15,fontWeight:700,marginBottom:16,color:K.alt}}>Plataforma</h4>
            <div style={{display:"flex",flexDirection:"column",gap:10,opacity:.7,fontSize:14}}>
              <span onClick={()=>setModalInfo("sobre")} className="hov" style={{cursor:"pointer", width:"fit-content"}}>Sobre o Eventos&Encontros</span>
              <span onClick={()=>setModalInfo("clientes")} className="hov" style={{cursor:"pointer", width:"fit-content"}}>Como Comprar</span>
              <span onClick={()=>navigate("/historia-rcc")} className="hov" style={{cursor:"pointer", width:"fit-content"}}>A História da RCC</span>
            </div>
          </div>
          <div>
            <h4 style={{fontSize:15,fontWeight:700,marginBottom:16,color:K.alt}}>Suporte & Jurídico</h4>
            <div style={{display:"flex",flexDirection:"column",gap:10,opacity:.7,fontSize:14}}>
              <span onClick={() => window.location.href = atob("bWFpbHRvOmRldi5sZW9jYXJ2YWxob0BnbWFpbC5jb20=")} className="hov" style={{cursor:"pointer", width:"fit-content"}}>Fale Conosco</span>
              <span onClick={()=>navigate("/termos")} className="hov" style={{cursor:"pointer", width:"fit-content"}}>Termos de Uso</span>
              <span onClick={()=>navigate("/privacidade")} className="hov" style={{cursor:"pointer", width:"fit-content"}}>Política de Privacidade</span>
              <span onClick={()=>navigate("/politica-devolucao")} className="hov" style={{cursor:"pointer", width:"fit-content"}}>Política de Devolução (CDC)</span>
              <span onClick={()=>navigate("/politica-de-seguranca")} className="hov" style={{cursor:"pointer", width:"fit-content"}}>Política de Segurança</span>
            </div>
          </div>
          <div>
            <h4 style={{fontSize:15,fontWeight:700,marginBottom:16,color:K.alt}}>Redes Sociais</h4>
            <div style={{display:"flex",gap:12}}>
              {/* Instagram */}
              <a href="https://instagram.com/SEU_INSTAGRAM" target="_blank" rel="noreferrer" className="hov" style={{color:"#fff", background:"rgba(255,255,255,0.1)", padding:10, borderRadius:K.r.m, display:"flex", alignItems:"center", justifyContent:"center"}}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
              </a>
              {/* WhatsApp */}
              <a href="https://wa.me/5500000000000" target="_blank" rel="noreferrer" className="hov" style={{color:"#fff", background:"rgba(255,255,255,0.1)", padding:10, borderRadius:K.r.m, display:"flex", alignItems:"center", justifyContent:"center"}}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
              </a>
              {/* Email */}
              <a href={atob("bWFpbHRvOmRldi5sZW9jYXJ2YWxob0BnbWFpbC5jb20=")} className="hov" style={{color:"#fff", background:"rgba(255,255,255,0.1)", padding:10, borderRadius:K.r.m, display:"flex", alignItems:"center", justifyContent:"center"}}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
              </a>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:10, marginTop:24}}>
              <img src="https://logodownload.org/wp-content/uploads/2019/06/mercado-pago-logo-0.png" alt="Mercado Pago" style={{width:80, filter:"brightness(0) invert(1)", opacity:0.7}}/>
            </div>
          </div>
        </div>
        <div style={{maxWidth:1060,margin:"40px auto 0",paddingTop:24,borderTop:"1px solid rgba(255,255,255,0.1)",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:14,opacity:.4,fontSize:13}}>
          <p>© {new Date().getFullYear()} Eventos e Encontros. Todos os direitos reservados.</p>
          <p>Desenvolvido para transformar vidas.</p>
        </div>
      </footer>
          </>
        } />
      </Routes>

      {checkout&&sel&&<Checkout evento={sel} onClose={()=>setCheckout(false)} onAuthNeeded={()=>{setCheckout(false);setAuthModal(true);}}/>}
      {authModal&&<AuthModal onClose={()=>setAuthModal(false)} onSuccess={()=>addToast(`Bem-vindo! 👋`)}/>}
      {meusIngressos&&user&&<MeusIngressos onClose={()=>setMeusIngressos(false)}/>}
      {toast&&<Toast {...toast} onClose={()=>setToast(null)}/>}

      {modalInfo && (
        <Modal onClose={()=>setModalInfo(null)} mw={500}>
          <div style={{padding:"32px 28px"}}>
            <h2 style={{fontFamily:"'Poppins',sans-serif",fontSize:22,marginBottom:14,color:K.ink}}>
              {modalInfo==="sobre"&&"Sobre a Plataforma"}
              {modalInfo==="clientes"&&"Para Clientes"}
            </h2>
            <div style={{fontSize:14,color:K.mid,lineHeight:1.7}}>
              {modalInfo==="sobre"&&<p>O <strong>Eventos & Encontros</strong> é um SaaS (Software as a Service) de ponta focado em revolucionar a gestão de tickets e ingressos. Nossa plataforma oferece uma solução robusta, moderna e de alto desempenho para criadores de eventos gerenciarem vendas de forma simples, integrando tecnologia serverless para pagamentos instantâneos e validação antifraude via QR Code.</p>}
              {modalInfo==="clientes"&&<p>Comprar seu ingresso aqui é super rápido e seguro!<br/><br/><b>Formas de Pagamento:</b><br/>Aceitamos PIX, Boleto e Cartão de Crédito em até 12x via Mercado Pago, com processamento 100% blindado.<br/><br/><b>Como funciona:</b><br/>1. Escolha o evento e clique em "Garantir ingresso".<br/>2. Realize o pagamento pelo ambiente seguro.<br/>3. Acesse a aba "Meus Ingressos" para ver seu ingresso digital com QR Code e apresentá-lo na portaria do evento!</p>}
            </div>
            <Btn full onClick={()=>setModalInfo(null)} sx={{marginTop:24}}>Entendi, Fechar</Btn>
          </div>
        </Modal>
      )}
      
      <Chatbox />
    </div>
  );
}

export default function App(){
  return<AuthProvider><AppInner/></AuthProvider>;
}
